(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabControls = api;
  root.PjDesignLabProcessing = {
    prepare: api.prepare,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var DEFAULT_MODE = 'background';
  var EXTRACTION_MODES = ['background', 'cutout'];
  var DEFAULT_STYLE = 'none';
  var STAMP_REFUSED_RETRY_MS = 5 * 60 * 1000;
  // Quiet time before stamping, so a burst of design changes is one resolve call.
  var STAMP_DEBOUNCE_MS = 600;

  // Pet artwork size is system-controlled: every pet fills its placement slot
  // at scale 1, and customers can't resize it. They can still drag it to a new
  // position (pj-design-lab-reposition.js), kept as an offset from the slot.
  function defaultAdjustment() {
    return { offsetX: 0, offsetY: 0, scale: 1 };
  }

  function isDefaultAdjustment(adjustment) {
    return !adjustment || (adjustment.offsetX === 0 && adjustment.offsetY === 0);
  }

  // Offsets are clamped so a pet can't be dragged off its flat-lay; scale is
  // always 1, whatever is passed in.
  function clampPosition(adjustment) {
    var source = adjustment || {};
    var offsetX = Number(source.offsetX);
    var offsetY = Number(source.offsetY);
    return {
      offsetX: Number.isFinite(offsetX) ? Math.max(-50, Math.min(50, offsetX)) : 0,
      offsetY: Number.isFinite(offsetY) ? Math.max(-50, Math.min(50, offsetY)) : 0,
      scale: 1,
    };
  }

  function normalizeMode(mode) {
    return mode === 'cutout' || mode === 'face_cutout' || mode === 'face-cutout'
      ? 'cutout'
      : DEFAULT_MODE;
  }

  function normalizeStyle(style) {
    var key = style == null ? '' : String(style).trim();
    return key === '' ? DEFAULT_STYLE : key;
  }

  // ---- Picture style catalog ----------------------------------------------
  // Shop JSON metafield cuddleclones.pj_design_lab_styles owns the list, so the
  // grid renders what marketing published rather than a fixed set. `key` is what
  // a pet stores and production reads; `treatment` names the art treatment the
  // theme draws for it. The set below is the dev/staging placeholder, standing in
  // until marketing publishes the launch catalog.
  var STYLE_NONE_KEY = 'none';
  var STYLE_NONE_LABEL = 'No Filter \u2014 original photo';
  var STYLE_RENDERER_FILTER = 'filter';
  var STYLE_RENDERER_GENERATED = 'generated';

  var DEFAULT_STYLE_CATALOG = [
    {
      key: STYLE_NONE_KEY,
      label: STYLE_NONE_LABEL,
      treatment: STYLE_NONE_KEY,
      swatch: ['#f6d2b0', '#d98c6a', '#7a5240'],
      swatchImage: '',
    },
    {
      key: 'bw',
      label: 'B&W',
      treatment: 'bw',
      swatch: ['#ffffff', '#9ca3af', '#111111'],
      swatchImage: '',
    },
    {
      key: 'color',
      label: 'Color',
      treatment: 'color',
      swatch: ['#f5c518', '#cb62d0', '#5562c8'],
      swatchImage: '',
    },
    {
      key: 'ink',
      label: 'Ink Wash',
      treatment: 'ink',
      swatch: ['#dfe7f0', '#7d93b0', '#33445a'],
      swatchImage: '',
    },
    {
      key: 'marker',
      label: 'Marker',
      treatment: 'marker',
      swatch: ['#ffd400', '#ff5c5c', '#2bbbad'],
      swatchImage: '',
    },
  ];

  // pj-design-lab-style.js owns every treatment and loads ahead of this file.
  function styleApi() {
    return root.PjDesignLabStyle || null;
  }

  // An entry naming a treatment this build cannot draw is dropped rather than
  // offered as a chip that would leave the portrait untouched.
  function styleTreatmentAvailable(treatment) {
    var api = styleApi();
    return api && typeof api.hasTreatment === 'function' ? api.hasTreatment(treatment) : true;
  }

  // Swatch colors are published as hex, never as CSS, so a merchant-edited value
  // can never reach a style attribute as arbitrary CSS.
  var HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

  function parseStyleSwatch(raw) {
    var list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
    var colors = [];
    list.forEach(function (candidate) {
      var color = String(candidate == null ? '' : candidate).trim();
      if (HEX_COLOR_PATTERN.test(color)) colors.push(color.toLowerCase());
    });
    return colors;
  }

  function styleSwatchGradient(colors) {
    if (!colors || !colors.length) return '';
    if (colors.length === 1) return colors[0];

    var stops = colors.map(function (color, index) {
      return color + ' ' + Math.round((index / (colors.length - 1)) * 100) + '%';
    });
    return 'linear-gradient(135deg, ' + stops.join(', ') + ')';
  }

  // No renderer means `filter`, so catalogs predating generated styles keep working.
  function normalizeRenderer(raw) {
    return normalizeCatalogKey(raw) === STYLE_RENDERER_GENERATED
      ? STYLE_RENDERER_GENERATED
      : STYLE_RENDERER_FILTER;
  }

  function parseStyleEntry(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (isCatalogEntryDisabled(raw.enabled)) return null;

    var key = normalizeCatalogKey(raw.key);
    var label = firstCatalogText(raw.label);
    if (!key || !label) return null;

    var renderer = normalizeRenderer(raw.renderer);
    var generated = renderer === STYLE_RENDERER_GENERATED;

    var treatment = normalizeCatalogKey(raw.treatment) || key;
    if (!generated && !styleTreatmentAvailable(treatment)) return null;

    var order = Number(raw.displayOrder);

    return {
      key: key,
      label: label,
      renderer: renderer,
      recipeKey: normalizeCatalogKey(raw.recipeKey) || key,
      treatment: generated ? '' : treatment,
      swatch: parseStyleSwatch(raw.swatch),
      swatchImage: firstCatalogText(raw.swatchImage, raw.swatchUrl),
      displayOrder: Number.isFinite(order) ? order : null,
    };
  }

  function styleCatalogList(raw) {
    var parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return [];
      }
    }
    if (!parsed) return [];

    var list = Array.isArray(parsed) ? parsed : parsed.styles;
    return Array.isArray(list) ? list : [];
  }

  function parseStyleCatalog(raw) {
    var list = styleCatalogList(raw);
    if (!list.length) return [];

    var seen = {};
    var entries = [];
    list.forEach(function (candidate, index) {
      var entry = parseStyleEntry(candidate);
      if (!entry || seen[entry.key]) return;
      seen[entry.key] = true;
      entry.index = index;
      entries.push(entry);
    });

    sortByDisplayOrder(entries);

    return entries.map(function (entry) {
      return {
        key: entry.key,
        label: entry.label,
        renderer: entry.renderer,
        recipeKey: entry.recipeKey,
        treatment: entry.treatment,
        swatch: entry.swatch,
        swatchImage: entry.swatchImage,
      };
    });
  }

  // No Filter is a permanent catalog entry, so leaving it out of a published
  // catalog does not delete it. Hiding it is a deliberate act: only an entry
  // that names the key and carries enabled:false takes it off the grid.
  function noFilterHidden(raw) {
    var list = styleCatalogList(raw);
    for (var index = 0; index < list.length; index += 1) {
      var entry = list[index];
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
      if (normalizeCatalogKey(entry.key) !== STYLE_NONE_KEY) continue;
      return isCatalogEntryDisabled(entry.enabled);
    }
    return false;
  }

  // No Filter is the way back to the untouched photo: a catalog can relabel or
  // re-swatch it, and can hide it, but never delete it or move it off the front
  // of the grid while it is visible.
  function resolveStyleCatalog(raw) {
    var published = parseStyleCatalog(raw);
    var entries = published.length ? published : DEFAULT_STYLE_CATALOG;
    var none = null;
    var rest = [];

    entries.forEach(function (entry) {
      if (entry.key === STYLE_NONE_KEY && !none) none = entry;
      else if (entry.key !== STYLE_NONE_KEY) rest.push(entry);
    });

    if (published.length && noFilterHidden(raw)) return rest;
    return [none || DEFAULT_STYLE_CATALOG[0]].concat(rest);
  }

  // The selection the studio falls back to: No Filter whenever the catalog
  // offers it, otherwise the first enabled style in display order, so hiding
  // No Filter never leaves a pet without a style it can render.
  function defaultStyleKey(catalog) {
    var entries = Array.isArray(catalog) ? catalog : [];
    for (var index = 0; index < entries.length; index += 1) {
      if (entries[index].key === STYLE_NONE_KEY) return STYLE_NONE_KEY;
    }
    return entries.length ? entries[0].key : DEFAULT_STYLE;
  }

  function styleKeys(catalog) {
    return (Array.isArray(catalog) ? catalog : []).map(function (entry) {
      return entry.key;
    });
  }

  function resolveStyle(catalog, key) {
    var keys = styleKeys(catalog);
    var normalized = normalizeCatalogKey(normalizeStyle(key));
    return keys.indexOf(normalized) >= 0 ? normalized : defaultStyleKey(catalog);
  }

  function styleEntry(catalog, key) {
    var entries = Array.isArray(catalog) ? catalog : [];
    for (var index = 0; index < entries.length; index += 1) {
      if (entries[index].key === key) return entries[index];
    }
    return null;
  }

  function isGeneratedStyle(catalog, key) {
    var entry = styleEntry(catalog, key);
    return Boolean(entry && entry.renderer === STYLE_RENDERER_GENERATED);
  }

  function styleRecipeKey(catalog, key) {
    var entry = styleEntry(catalog, key);
    return (entry && (entry.recipeKey || entry.key)) || key;
  }

  function generatedRecipeKeys(catalog) {
    return (Array.isArray(catalog) ? catalog : [])
      .filter(function (entry) {
        return entry.renderer === STYLE_RENDERER_GENERATED;
      })
      .map(function (entry) {
        return entry.recipeKey || entry.key;
      });
  }

  function styleTreatment(catalog, key) {
    var entries = Array.isArray(catalog) ? catalog : [];
    for (var index = 0; index < entries.length; index += 1) {
      if (entries[index].key === key) return entries[index].treatment || key;
    }
    return STYLE_NONE_KEY;
  }

  // ---- Accessory catalog --------------------------------------------------
  // Shop JSON metafield cuddleclones.pj_design_lab_accessories owns the list,
  // so the selector renders what marketing published rather than a fixed set.
  // Each entry carries a stable `key` (what a pet stores and what production
  // reads - labels are mutable, keys are not), a `label`, an `icon` for the
  // picker chip, and an `asset` - the artwork that goes on the pet, which this
  // step only passes through to the composite renderer.
  var ACCESSORY_NONE_KEY = 'none';

  // Both percentages are relative to the pet's own box, exactly as the
  // wireframe's ACCESSORIES entries express them: `width` is the accessory's
  // width against the pet's width, `bottom` is where its bottom edge sits
  // measured up from the bottom of that box.
  // Geometry belongs to whoever publishes the artwork, so there is no default
  // here: an entry supplies its own `placement`, or inherits the catalog's
  // `defaultPlacement`, or gets no overlay at all. `fallback` is the catalog's
  // default, never a value from this file.
  function parseAccessoryPlacement(raw, fallback) {
    var source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    var base = fallback && typeof fallback === 'object' ? fallback : {};

    var width = Number(source.width);
    if (!Number.isFinite(width) || width <= 0) width = Number(base.width);
    var bottom = Number(source.bottom);
    if (!Number.isFinite(bottom)) bottom = Number(base.bottom);

    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(bottom)) return null;
    return { width: width, bottom: bottom };
  }

  // How far to raise an accessory when a pet is a Remove Background portrait
  // rather than a face cutout: the portrait keeps the whole subject plus
  // transparent padding, so the head sits lower in its box. It is one number
  // for the whole catalog, published as `backgroundLift`; unset means no
  // adjustment.
  function parseAccessorySettings(raw) {
    var parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return { backgroundLift: 0 };
      }
    }
    var source = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    var lift = Number(source.backgroundLift);

    return { backgroundLift: Number.isFinite(lift) ? lift : 0 };
  }

  // The artboard alternative to `placement`: the accessory artwork is a whole
  // 3" artboard drawn exactly as Design exported it, and the pet is fitted into
  // this box on it. Values are inches from the artboard's top-left, as the
  // positioning sheet states them.
  function parseAccessoryPetBox(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    var box = {
      x: Number(raw.x),
      y: Number(raw.y),
      width: Number(raw.width),
      height: Number(raw.height),
      anchor: raw.anchor === 'center' ? 'center' : 'top',
    };
    if (raw.fit === 'height') box.fit = 'height';
    var valid = [box.x, box.y].every(Number.isFinite) && box.width > 0 && box.height > 0;
    return valid ? box : null;
  }

  function liftAccessoryPlacement(placement, mode, lift) {
    var offset = Number(lift);
    if (!placement || mode === 'cutout' || !Number.isFinite(offset) || offset === 0) {
      return placement;
    }
    return { width: placement.width, bottom: placement.bottom + offset };
  }

  // displayOrder is optional per entry; anything without one keeps its position
  // in the published list.
  function sortByDisplayOrder(entries) {
    return entries.sort(function (left, right) {
      var a = left.displayOrder == null ? left.index : left.displayOrder;
      var b = right.displayOrder == null ? right.index : right.displayOrder;
      return a - b;
    });
  }

  function normalizeCatalogKey(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeAccessoryKey(value) {
    return normalizeCatalogKey(value);
  }

  // Liquid renders a false boolean as the string 'false', so the disabled
  // check covers both forms rather than testing truthiness.
  function isCatalogEntryDisabled(value) {
    return value === false || value === 'false' || value === 0 || value === '0';
  }

  function firstCatalogText() {
    for (var index = 0; index < arguments.length; index += 1) {
      var value = arguments[index];
      if (value == null) continue;
      var text = String(value).trim();
      if (text) return text;
    }
    return '';
  }

  // The back layer is published as its own object - { enabled, image } - so
  // marketing can park artwork and switch it off without deleting the URL. It
  // flattens to a single string here: an absent, disabled or image-less object
  // all mean the same thing to the renderer, which is that there is no back
  // layer to draw.
  function parseAccessoryBackLayer(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '';
    if (isCatalogEntryDisabled(raw.enabled)) return '';
    return firstCatalogText(raw.image, raw.asset, raw.url);
  }

  // Every other field the parser emits is named after the field it read, so
  // feeding a parsed entry back through the parser returns it unchanged. This
  // one is renamed on the way out, so it needs its own name accepted on the
  // way in to keep the parser total rather than order-dependent.
  function accessoryBackAsset(raw) {
    return firstCatalogText(parseAccessoryBackLayer(raw.back_layer), raw.backAsset);
  }

  function parseAccessoryEntry(raw, defaultPlacement) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (isCatalogEntryDisabled(raw.enabled)) return null;

    var key = normalizeAccessoryKey(raw.key);
    var label = firstCatalogText(raw.label);
    if (!key || !label) return null;

    // The chip wants the small icon and the pet wants the full artwork, but
    // either field stands in for a missing sibling. "None" is the one entry
    // that needs no artwork at all - its circle-slash is drawn in CSS - so
    // every other entry without one is dropped rather than rendered as an
    // empty chip.
    var icon = firstCatalogText(raw.icon, raw.iconUrl, raw.asset, raw.overlayAsset);
    var backAsset = accessoryBackAsset(raw);
    // An entry that publishes a back layer states its layers outright, so the
    // icon fallback is dropped for it - otherwise a back layer published
    // without front artwork would draw its chip glyph on the pet as a front
    // layer it never asked for.
    var asset = backAsset
      ? firstCatalogText(raw.asset, raw.overlayAsset)
      : firstCatalogText(raw.asset, raw.overlayAsset, raw.icon, raw.iconUrl);
    if (!icon && key !== ACCESSORY_NONE_KEY) return null;

    var order = Number(raw.displayOrder);

    return {
      key: key,
      label: label,
      icon: icon,
      asset: asset,
      // Flattened from the catalog's back_layer object: the artwork that wraps
      // behind the pet (an elf hat's crown), or '' when the catalog publishes
      // no back layer or has switched it off.
      backAsset: backAsset,
      placement: parseAccessoryPlacement(raw.placement, defaultPlacement),
      petBox: parseAccessoryPetBox(raw.petBox),
      displayOrder: Number.isFinite(order) ? order : null,
    };
  }

  function parseAccessoryCatalog(raw) {
    var parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return [];
      }
    }
    if (!parsed) return [];

    var list = Array.isArray(parsed) ? parsed : parsed.accessories;
    if (!Array.isArray(list)) return [];

    var defaultPlacement = Array.isArray(parsed)
      ? null
      : parseAccessoryPlacement(parsed.defaultPlacement, null);

    var seen = {};
    var entries = [];
    list.forEach(function (candidate, index) {
      var entry = parseAccessoryEntry(candidate, defaultPlacement);
      if (!entry || seen[entry.key]) return;
      seen[entry.key] = true;
      entry.index = index;
      entries.push(entry);
    });

    sortByDisplayOrder(entries);

    // Nothing to choose between when "None" is all the catalog publishes, so
    // the step stays hidden instead of offering a single chip.
    var hasAccessory = entries.some(function (entry) {
      return entry.key !== ACCESSORY_NONE_KEY;
    });
    if (!hasAccessory) return [];

    // Order, labels and the presence of "None" are all the catalog's to
    // decide - it publishes "None" as a permanent entry, so nothing is
    // invented here.
    return entries.map(function (entry) {
      var out = {
        key: entry.key,
        label: entry.label,
        icon: entry.icon,
        asset: entry.asset,
        backAsset: entry.backAsset,
        placement: entry.placement,
      };
      if (entry.petBox) out.petBox = entry.petBox;
      return out;
    });
  }

  function accessoryKeys(catalog) {
    return (Array.isArray(catalog) ? catalog : []).map(function (entry) {
      return entry.key;
    });
  }

  function defaultAccessoryKey(catalog) {
    var keys = accessoryKeys(catalog);
    if (!keys.length) return ACCESSORY_NONE_KEY;
    return keys.indexOf(ACCESSORY_NONE_KEY) >= 0 ? ACCESSORY_NONE_KEY : keys[0];
  }

  function resolveAccessory(catalog, key) {
    var keys = accessoryKeys(catalog);
    var normalized = normalizeAccessoryKey(key);
    return keys.indexOf(normalized) >= 0 ? normalized : defaultAccessoryKey(catalog);
  }

  function stepNumbers(isMulti, extractionVisible, filtersVisible, accessoriesVisible) {
    var priorSteps = (isMulti ? 1 : 0) + (extractionVisible ? 1 : 0);
    var styleVisible = filtersVisible !== false;
    var styleNum = priorSteps + 1;
    var accessoryStepVisible = accessoriesVisible !== false;
    return {
      extraction: isMulti ? 2 : 1,
      style: styleNum,
      accessory: accessoryStepVisible ? styleNum + (styleVisible ? 1 : 0) : styleNum,
    };
  }

  function defaultStyleImage(imageUrl, treatment, recipeVersion) {
    var api = styleApi();
    if (!api || typeof api.styleImage !== 'function') {
      return Promise.reject(new Error('Picture styles are not available'));
    }
    return api.styleImage(imageUrl, treatment, recipeVersion);
  }

  function createPet(sourceImage) {
    return {
      sourceImage: sourceImage,
      mode: DEFAULT_MODE,
      preferredMode: DEFAULT_MODE,
      style: DEFAULT_STYLE,
      preferredStyle: DEFAULT_STYLE,
      accessory: ACCESSORY_NONE_KEY,
      processedByMode: {},
      // Extraction requests on the wire, by mode, so a second caller shares one.
      pendingByMode: {},
      processedImage: '',
      status: 'idle',
      error: null,
      requestId: 0,
      // `styledFor` is the style-and-portrait pair styledImage was made from, so
      // a stale treatment is thrown away rather than shown.
      styledImage: '',
      styledFor: '',
      styleStatus: 'idle',
      styleError: null,
      styleRequestId: 0,
      // Every style this pet has generated, keyed by cache key, so returning to
      // one already prepared is instant rather than a second round trip. The
      // single `styledImage` slot above stays as the view of the current style.
      styleCache: {},
      // Per-style lifecycle for the chips: 'idle' | 'pending' | 'ready' | 'error'.
      styleStates: {},
      // Drag-to-reposition offset (scale is always 1). Resets whenever the pet
      // is recreated - a photo replace or an extraction-mode change.
      adjustment: defaultAdjustment(),
      accessoryAdjust: defaultAccessoryAdjust(),
    };
  }

  // TEST ONLY: shopper's manual accessory placement on the 3" artboard, made
  // by clicking and dragging the accessory on the preview
  // (pj-design-lab-accessory-handles.js). One { x, y, scale, rotation } per
  // layer - `front` (the elf's hat) and `back` (its collar) move separately -
  // or null for a layer left where the catalog puts it. Board fractions and
  // degrees. Preview only - not sent to cart properties or CORS.
  var ACCESSORY_LAYERS = ['front', 'back'];

  function defaultAccessoryAdjust() {
    return { front: null, back: null };
  }

  function clampLayerAdjust(raw) {
    var value = raw || {};
    var clamp = function (number, min, max, fallback) {
      var n = Number(number);
      return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
    };
    return {
      x: clamp(value.x, -0.4, 0.4, 0),
      y: clamp(value.y, -0.4, 0.4, 0),
      scale: clamp(value.scale, 0.5, 2, 1),
      rotation: clamp(value.rotation, -180, 180, 0),
    };
  }

  function accessoryAdjusted(adjust) {
    return Boolean(adjust && (adjust.front || adjust.back));
  }

  function replacePetSource(current, sourceImage) {
    var replacement = createPet(sourceImage);
    if (!current) return replacement;

    replacement.mode = current.mode;
    replacement.preferredMode = current.preferredMode;
    replacement.style = current.style;
    replacement.preferredStyle = current.preferredStyle;
    replacement.accessory = current.accessory;
    return replacement;
  }

  function createPetStore(processPet, onChange, options) {
    var styleImage = (options && options.styleImage) || defaultStyleImage;
    var generatedPipeline = (options && options.generatedStyles) || null;
    var state = {
      activePetIdx: 0,
      accessories: [],
      styles: [],
      accessorySettings: { backgroundLift: 0 },
      color: '',
      extractionEnabled: true,
      filtersEnabled: true,
      accessoriesEnabled: true,
      mediaId: '',
      // Bumped by marketing when the generation recipe changes, so cached art
      // made under the old recipe is never served.
      recipeVersion: (options && options.recipeVersion) || '1',
      // Generate every style during preparation so switching is instant, versus
      // generating on selection. Off by default: with browser filters the work
      // is cheap and instant either way, and the ticket leaves eager-vs-lazy
      // open on cost grounds until generated styles land. Flip it on with the
      // shop metafield once generation is real.
      eagerStyles: Boolean(options && options.eagerStyles),
      // Also extract every other mode during preparation, so switching
      // background <-> face cutout is instant. Shop metafield
      // cuddleclones.pj_design_lab_eager_extraction; off by default.
      eagerExtraction: Boolean(options && options.eagerExtraction),
      styleLatency: { perStyle: {}, totalMs: 0 },
      // A stalled provider must reach a recoverable error rather than hang.
      generationTimeoutMs:
        (options && Number(options.generationTimeoutMs)) || 60000,
      // How many generations may be in flight at once.
      generationConcurrency:
        (options && Number(options.generationConcurrency)) || 3,
      // Real preparation progress: units completed out of units expected, so the
      // bar reflects work rather than a timer.
      preparation: { completed: 0, total: 0 },
      pets: [],
    };

    function notify() {
      if (typeof onChange === 'function') onChange(snapshot());
    }

    function snapshot() {
      return {
        accessories: state.accessories,
        accessorySettings: state.accessorySettings,
        activePetIdx: state.activePetIdx,
        color: state.color,
        extractionEnabled: state.extractionEnabled,
        filtersEnabled: state.filtersEnabled,
        accessoriesEnabled: state.accessoriesEnabled,
        mediaId: state.mediaId,
        recipeVersion: state.recipeVersion,
        styles: state.styles,
        pets: state.pets.map(function (pet) {
          return {
            sourceImage: pet.sourceImage,
            mode: pet.mode,
            preferredMode: pet.preferredMode,
            style: pet.style,
            preferredStyle: pet.preferredStyle,
            accessory: pet.accessory,
            processedImage: pet.processedImage,
            styledImage: pet.styledImage,
            styleStatus: pet.styleStatus,
            styleError: pet.styleError,
            // Per-style lifecycle for the chips. Copied, not shared, so a
            // consumer holding an old snapshot cannot mutate live state.
            styleStates: Object.assign({}, pet.styleStates),
            status: pet.status,
            error: pet.error,
            adjustment: pet.adjustment,
            accessoryAdjust: pet.accessoryAdjust,
          };
        }),
      };
    }

    function reconcile(context) {
      var nextContext = context || {};
      var extractionEnabled =
        typeof nextContext.extractionEnabled === 'boolean'
          ? nextContext.extractionEnabled
          : state.extractionEnabled;
      var filtersEnabled =
        typeof nextContext.filtersEnabled === 'boolean'
          ? nextContext.filtersEnabled
          : state.filtersEnabled;
      var accessoriesEnabled =
        typeof nextContext.accessoriesEnabled === 'boolean'
          ? nextContext.accessoriesEnabled
          : state.accessoriesEnabled;
      var requestedCount = Math.max(0, Number(nextContext.petCount) || 0);
      var images = Array.isArray(nextContext.petImages)
        ? nextContext.petImages.slice(0, requestedCount || nextContext.petImages.length)
        : [];
      var previous = state.pets;

      state.pets = images
        .filter(function (sourceImage) {
          return Boolean(sourceImage);
        })
        .map(function (sourceImage, index) {
          var current = previous[index];
          return current && current.sourceImage === sourceImage
            ? current
            : replacePetSource(current, sourceImage);
        });

      if (extractionEnabled !== state.extractionEnabled) {
        state.pets.forEach(function (pet) {
          if (!extractionEnabled) {
            pet.preferredMode = pet.mode;
            pet.mode = DEFAULT_MODE;
          } else {
            pet.mode = pet.preferredMode || DEFAULT_MODE;
          }
          pet.processedImage = pet.processedByMode[pet.mode] || '';
          pet.error = null;
          pet.status = 'idle';
        });
      } else if (!extractionEnabled) {
        state.pets.forEach(function (pet) {
          pet.mode = DEFAULT_MODE;
          pet.processedImage = pet.processedByMode[DEFAULT_MODE] || '';
        });
      }
      state.extractionEnabled = extractionEnabled;

      // Style is render-time, so a filters flip never invalidates the processed
      // images the block above manages.
      if (filtersEnabled !== state.filtersEnabled) {
        state.pets.forEach(function (pet) {
          if (!filtersEnabled) {
            pet.preferredStyle = pet.style;
            pet.style = DEFAULT_STYLE;
          } else {
            pet.style = pet.preferredStyle || DEFAULT_STYLE;
          }
        });
      } else if (!filtersEnabled) {
        state.pets.forEach(function (pet) {
          pet.style = DEFAULT_STYLE;
        });
      }
      state.filtersEnabled = filtersEnabled;

      if (accessoriesEnabled !== state.accessoriesEnabled) {
        state.pets.forEach(function (pet) {
          if (!accessoriesEnabled) {
            pet.accessory = ACCESSORY_NONE_KEY;
          }
        });
      } else if (!accessoriesEnabled) {
        state.pets.forEach(function (pet) {
          pet.accessory = ACCESSORY_NONE_KEY;
        });
      }
      state.accessoriesEnabled = accessoriesEnabled;

      // Marketing can retire a style under a pet that already holds it, so each
      // pet is re-pointed at an entry that still exists (or back to No Filter).
      if (nextContext.styles != null) state.styles = resolveStyleCatalog(nextContext.styles);
      if (state.styles.length) {
        state.pets.forEach(function (pet) {
          pet.preferredStyle = resolveStyle(state.styles, pet.preferredStyle);
          pet.style = state.filtersEnabled ? resolveStyle(state.styles, pet.style) : DEFAULT_STYLE;
        });
      }

      // The catalog is marketing-owned and can change under a selection a pet
      // already holds, so each pet is re-pointed at an entry that still exists
      // (or the default) rather than keeping a key nothing can render.
      if (nextContext.accessories != null) {
        state.accessories = parseAccessoryCatalog(nextContext.accessories);
        state.accessorySettings = parseAccessorySettings(nextContext.accessories);
      }
      if (state.accessoriesEnabled) {
        state.pets.forEach(function (pet) {
          pet.accessory = resolveAccessory(state.accessories, pet.accessory);
        });
      }

      state.activePetIdx = Math.min(
        Math.max(0, state.activePetIdx),
        Math.max(0, state.pets.length - 1)
      );
      state.color = nextContext.color == null ? state.color : String(nextContext.color);
      state.mediaId =
        nextContext.mediaId == null ? state.mediaId : String(nextContext.mediaId);
      notify();
      state.pets.forEach(function (pet, index) {
        requestStyle(index).catch(function () {
          return '';
        });
      });
      return snapshot();
    }

    // "None" draws nothing, and an entry with no artwork cannot be drawn, so
    // both resolve to null rather than an overlay the renderer has to guard.
    function accessoryOverlay(pet) {
      if (!state.accessoriesEnabled) return null;
      if (!pet.accessory || pet.accessory === ACCESSORY_NONE_KEY) return null;

      for (var index = 0; index < state.accessories.length; index += 1) {
        var entry = state.accessories[index];
        if (entry.key !== pet.accessory) continue;
        if ((!entry.asset && !entry.backAsset) || (!entry.placement && !entry.petBox)) return null;
        var overlay = {
          asset: entry.asset,
          // Both layers share one placement - they are the same artwork cut in
          // two, so separate geometry would shear the halves apart.
          backAsset: entry.backAsset,
          placement: liftAccessoryPlacement(
            entry.placement,
            pet.mode,
            state.accessorySettings.backgroundLift
          ),
        };
        // Wins over `placement` in the renderer. The pet is fitted into the
        // box whole, so backgroundLift has nothing to correct here.
        if (entry.petBox) overlay.petBox = entry.petBox;
        return overlay;
      }
      return null;
    }

    function rendererContext() {
      return {
        petCount: state.pets.length,
        petImages: state.pets.map(function (pet) {
          return pet.sourceImage;
        }),
        activePet: state.activePetIdx,
        color: state.color,
        mediaId: state.mediaId,
        petSettings: state.pets.map(function (pet) {
          var adjustment = pet.adjustment || defaultAdjustment();
          return {
            extractionMode: pet.mode === 'cutout' ? 'face-cutout' : 'background',
            style: pet.style,
            accessory: pet.accessory,
            accessoryOverlay: accessoryOverlay(pet),
            processedImage: pet.styledImage || pet.processedImage,
            accessoryAdjust: accessoryAdjusted(pet.accessoryAdjust) ? pet.accessoryAdjust : null,
            processingError: Boolean(pet.error),
            // A failed style shows nothing rather than another image in its place.
            styleUnavailable: pet.styleStatus === 'error',
            offsetX: adjustment.offsetX,
            offsetY: adjustment.offsetY,
          };
        }),
      };
    }

    function selectPet(index) {
      var nextIndex = Number(index);
      if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= state.pets.length) {
        return false;
      }
      state.activePetIdx = nextIndex;
      notify();
      return true;
    }

    function setActiveStyle(style) {
      var pet = state.pets[state.activePetIdx];
      if (!pet) return false;

      var normalized = state.styles.length
        ? resolveStyle(state.styles, style)
        : normalizeStyle(style);
      pet.preferredStyle = normalized;
      pet.style = state.filtersEnabled ? normalized : DEFAULT_STYLE;
      notify();
      return true;
    }

    // (cutout, style key, recipe version): a recipe change must not serve art
    // generated under the previous one, so bumping the version invalidates every
    // cached result without touching the entries themselves.
    function styleCacheKeyFor(pet, style, source) {
      return style + '|' + state.recipeVersion + '|' + (source || pet.processedImage);
    }

    function styleCacheKey(pet) {
      return styleCacheKeyFor(pet, pet.style);
    }

    /** Styles worth preparing up front: the catalog minus the no-op default. */
    function generatableStyles() {
      var keys = [];
      state.styles.forEach(function (entry) {
        var key = entry && entry.key;
        if (!key || key === DEFAULT_STYLE) return;
        if (
          entry.renderer !== STYLE_RENDERER_GENERATED &&
          !styleTreatmentAvailable(styleTreatment(state.styles, key))
        ) {
          return;
        }
        if (keys.indexOf(key) === -1) keys.push(key);
      });
      return keys;
    }

    // One limit for every style; the theme guarantees it outlasts the 20s notice.
    function styleTimeoutMs() {
      return state.generationTimeoutMs;
    }

    function styleAssetFor(pet, style, options, source) {
      var portrait = source || pet.processedImage;
      if (!isGeneratedStyle(state.styles, style)) {
        return styleImage(
          portrait,
          styleTreatment(state.styles, style),
          state.recipeVersion
        );
      }

      if (!generatedPipeline || typeof generatedPipeline.resolveStyle !== 'function') {
        return Promise.reject(new Error('Generated styles are not configured'));
      }
      if (
        typeof generatedPipeline.configured === 'function' &&
        !generatedPipeline.configured()
      ) {
        return Promise.reject(new Error('Generated styles are not configured'));
      }

      return generatedPipeline.resolveStyle(
        portrait,
        styleRecipeKey(state.styles, style),
        generatedRecipeKeys(state.styles),
        options
      );
    }

    /**
     * True when nothing is left to prepare: every pet has its cutout and, where
     * a style is selected, that style's art already cached. The modal uses this
     * to skip the loader on reopen rather than replay a wait for work already
     * done (the wireframe's allStylesReady / showReadyPreview path).
     */
    function isPreparedForDisplay() {
      if (!state.pets.length) return false;
      return state.pets.every(function (pet) {
        if (!pet.processedImage || pet.status === 'processing') return false;
        if (!state.filtersEnabled || pet.style === DEFAULT_STYLE) return true;
        return Boolean(pet.styleCache[styleCacheKey(pet)]);
      });
    }

    function styleStateFor(pet, style) {
      return pet.styleStates[styleCacheKeyFor(pet, style)] || 'idle';
    }

    /**
     * A stalled provider must reach a recoverable error rather than hang for
     * ever, so every generation races a bounded timeout. Rejecting frees the
     * chip into its error state, where retry is offered.
     */
    function withTimeout(promise, ms, label) {
      if (!(ms > 0)) return Promise.resolve(promise);
      return new Promise(function (resolve, reject) {
        var settled = false;
        var timer = root.setTimeout(function () {
          if (settled) return;
          settled = true;
          reject(new Error(label + ' timed out after ' + ms + 'ms'));
        }, ms);

        Promise.resolve(promise).then(
          function (value) {
            if (settled) return;
            settled = true;
            root.clearTimeout(timer);
            resolve(value);
          },
          function (error) {
            if (settled) return;
            settled = true;
            root.clearTimeout(timer);
            reject(error);
          }
        );
      });
    }

    /**
     * "Ready to display" includes image loading, not just generation, so a
     * result is decoded before it counts as done. Failure to decode is not
     * failure to generate: the art is still cached and usable.
     */
    function decodeAhead(dataUrl) {
      if (!dataUrl || typeof root.Image !== 'function') return Promise.resolve();
      return new Promise(function (resolve) {
        var image = new root.Image();
        image.onload = function () {
          resolve();
        };
        image.onerror = function () {
          resolve();
        };
        image.src = dataUrl;
        if (image.decode) {
          image.decode().then(resolve, function () {
            resolve();
          });
        }
      });
    }

    /**
     * Runs tasks with a bounded number in flight. Serial preparation multiplies
     * a per-style round trip by pets x styles, which cannot meet the 10s target
     * once generation is a network call; unbounded would hammer the provider and
     * spike memory on mobile.
     */
    function runBounded(tasks, limit) {
      var width = Math.max(1, Number(limit) || 1);
      var next = 0;

      function worker() {
        if (next >= tasks.length) return Promise.resolve();
        var task = tasks[next];
        next += 1;
        return Promise.resolve()
          .then(task)
          .catch(function () {
            return null;
          })
          .then(worker);
      }

      var workers = [];
      for (var i = 0; i < Math.min(width, tasks.length); i += 1) {
        workers.push(worker());
      }
      return Promise.all(workers);
    }

    function now() {
      var perf = root.performance;
      return perf && typeof perf.now === 'function' ? perf.now() : Date.now();
    }

    function clearPetStyle(pet) {
      if (!pet.styledImage && !pet.styledFor && pet.styleStatus === 'idle' && !pet.styleError) {
        return false;
      }
      pet.styledImage = '';
      pet.styledFor = '';
      pet.styleStatus = 'idle';
      pet.styleError = null;
      return true;
    }

    // A shopper can switch styles faster than the treatments finish, so a response
    // is only accepted while it still answers the pet's current style and portrait.
    function isCurrentStyleRequest(index, pet, key, requestId) {
      var current = state.pets[index];
      return Boolean(
        current &&
          current === pet &&
          pet.styleRequestId === requestId &&
          styleCacheKey(pet) === key
      );
    }

    // A treatment of the portrait the extraction step produced, never a second
    // extraction: it redraws from `processedImage` and leaves that image - and so
    // what checkout persists - untouched.
    async function requestStyle(index, options) {
      var pet = state.pets[index];
      if (!pet) return '';

      var treatment = styleTreatment(state.styles, pet.style);
      var source = pet.processedImage;
      if (!source || pet.style === DEFAULT_STYLE || treatment === DEFAULT_STYLE) {
        if (clearPetStyle(pet)) notify();
        return '';
      }

      var key = styleCacheKey(pet);

      // Already prepared - either by this pet's own earlier selection or by the
      // eager pass after upload - so the switch costs nothing.
      var hit = pet.styleCache[key];
      if (hit) {
        var changed = false;
        if (pet.styledImage !== hit || pet.styledFor !== key) {
          pet.styledImage = hit;
          pet.styledFor = key;
          changed = true;
        }
        if (pet.styleStatus !== 'idle' || pet.styleError) {
          pet.styleStatus = 'idle';
          pet.styleError = null;
          changed = true;
        }
        if (changed) notify();
        return hit;
      }

      var requestId = ++pet.styleRequestId;
      pet.styleStatus = 'processing';
      pet.styleError = null;
      pet.styleStates[key] = 'pending';
      notify();

      var startedAt = now();
      try {
        var styled = await withTimeout(
          styleAssetFor(pet, pet.style, options),
          styleTimeoutMs(pet.style),
          'Style "' + pet.style + '"'
        );
        if (!styled) throw new Error('Styling returned no image');
        await decodeAhead(styled);

        // Cached and timed even when the selection has moved on: the work is
        // done and throwing it away would make returning to this style pay twice.
        pet.styleCache[key] = styled;
        pet.styleStates[key] = 'ready';
        recordStyleLatency(pet.style, now() - startedAt);

        if (!isCurrentStyleRequest(index, pet, key, requestId)) return styled;

        pet.styledImage = styled;
        pet.styledFor = key;
        pet.styleStatus = 'idle';
        pet.styleError = null;
        notify();
        return styled;
      } catch (error) {
        var paused = Boolean(error && error.reason === 'paused');
        pet.styleStates[key] = paused ? 'idle' : 'error';
        if (!isCurrentStyleRequest(index, pet, key, requestId)) {
          notify();
          return '';
        }

        pet.styledImage = '';
        pet.styledFor = '';
        pet.styleStatus = paused ? 'idle' : 'error';
        pet.styleError = error || new Error('Styling failed');
        notify();
        return '';
      }
    }

    /**
     * Prepares every catalog style for one pet so switching between them costs
     * nothing (the ticket's "generate all available styles after photo upload").
     * Failures are per style: one style failing leaves the others cached and
     * usable, and the chip for the failed one keeps its error state.
     */
    // `source` prepares a portrait other than the one on show (the warmed face
    // cutout); that work is background, so it stays out of the loader progress.
    async function prepareAllStyles(index, source) {
      var pet = state.pets[index];
      var portrait = source || (pet && pet.processedImage);
      if (!pet || !portrait) return;

      var pending = generatableStyles().filter(function (style) {
        var key = styleCacheKeyFor(pet, style, portrait);
        return !pet.styleCache[key] && pet.styleStates[key] !== 'pending';
      });

      var tasks = pending.map(function (style) {
        return function () {
          var key = styleCacheKeyFor(pet, style, portrait);
          pet.styleStates[key] = 'pending';
          notify();

          var startedAt = now();
          return withTimeout(
            styleAssetFor(pet, style, undefined, portrait),
            styleTimeoutMs(style),
            'Style "' + style + '"'
          )
            .then(function (styled) {
              if (!styled) throw new Error('Styling returned no image');
              return decodeAhead(styled).then(function () {
                pet.styleCache[key] = styled;
                pet.styleStates[key] = 'ready';
                recordStyleLatency(style, now() - startedAt);
              });
            })
            .catch(function (error) {
              pet.styleStates[key] = error && error.reason === 'paused' ? 'idle' : 'error';
            })
            .then(function () {
              if (!source) advancePreparation();
              notify();
            });
        };
      });

      return runBounded(tasks, state.generationConcurrency);
    }

    /**
     * Prepares every style for every pet, and reports the total time until the
     * whole set is ready to display - the number the 10s target and the 20s
     * delay threshold are both measured against.
     */
    function prepareAllStylesForAllPets() {
      var startedAt = now();
      state.styleLatency = { perStyle: {}, totalMs: 0 };

      // Pets run together rather than one after another: serial preparation
      // multiplies a per-style round trip by pets x styles. Concurrency stays
      // bounded inside each pet's pass.
      return Promise.all(
        state.pets.map(function (_pet, index) {
          return prepareAllStyles(index);
        })
      ).then(function () {
        state.styleLatency.totalMs = Math.round(now() - startedAt);
        emitStyleLatency();
        return state.styleLatency;
      });
    }

    /**
     * Progress is reported as completed units out of expected units, and a unit
     * only counts once its asset is cached and decoded - so the bar can never
     * reach complete before the work has actually arrived.
     */
    function beginPreparation(total) {
      state.preparation = { completed: 0, total: Math.max(0, total) };
      emitPreparationProgress();
    }

    function advancePreparation() {
      if (!state.preparation.total) return;
      state.preparation.completed = Math.min(
        state.preparation.completed + 1,
        state.preparation.total
      );
      emitPreparationProgress();
    }

    function emitPreparationProgress() {
      if (typeof root.CustomEvent !== 'function' || !root.dispatchEvent) return;
      var total = state.preparation.total;
      try {
        root.dispatchEvent(
          new root.CustomEvent('pj-design-lab:preparation-progress', {
            detail: {
              completed: state.preparation.completed,
              total: total,
              ratio: total ? state.preparation.completed / total : 0,
            },
          })
        );
      } catch (error) {
        /* progress reporting must never break the Lab */
      }
    }

    function recordStyleLatency(style, ms) {
      if (!state.styleLatency) state.styleLatency = { perStyle: {}, totalMs: 0 };
      var bucket = state.styleLatency.perStyle[style];
      var rounded = Math.round(ms);
      if (!bucket) {
        state.styleLatency.perStyle[style] = { count: 1, totalMs: rounded, maxMs: rounded };
        return;
      }
      bucket.count += 1;
      bucket.totalMs += rounded;
      bucket.maxMs = Math.max(bucket.maxMs, rounded);
    }

    // Measurement is a deliverable of this story, so the numbers leave the store
    // as an event rather than only a console line an operator has to be watching.
    function emitStyleLatency() {
      if (typeof root.CustomEvent !== 'function' || !root.dispatchEvent) return;
      try {
        root.dispatchEvent(
          new root.CustomEvent('pj-design-lab:style-latency', {
            detail: JSON.parse(JSON.stringify(state.styleLatency)),
          })
        );
      } catch (error) {
        /* measurement must never break the Lab */
      }
    }

    function changeActiveStyle(style) {
      // A style still being prepared cannot be chosen, so Add to Cart never
      // waits on it and never checks out art that has not arrived.
      var current = state.pets[state.activePetIdx];
      if (current && styleStateFor(current, style) === 'pending') return Promise.resolve('');
      if (!setActiveStyle(style)) return Promise.resolve('');
      return requestStyle(state.activePetIdx).catch(function () {
        return '';
      });
    }

    function retryActiveStyle() {
      var pet = state.pets[state.activePetIdx];
      if (!pet) return Promise.resolve('');
      pet.styledFor = '';
      pet.styleError = null;
      return requestStyle(state.activePetIdx, { retry: true }).catch(function () {
        return '';
      });
    }

    // An accessory is an overlay, never a reprocess: the choice lands in
    // state and the composite redraws from the same processed cutout.
    function setActiveAccessory(key) {
      if (!state.accessoriesEnabled) return false;

      var pet = state.pets[state.activePetIdx];
      if (!pet || !state.accessories.length) return false;

      var resolved = resolveAccessory(state.accessories, key);
      if (pet.accessory === resolved) return true;
      pet.accessory = resolved;
      // A different accessory starts from its catalog placement.
      pet.accessoryAdjust = defaultAccessoryAdjust();
      notify();
      return true;
    }

    // TEST ONLY: moves one layer ('front' or 'back') of the active pet's
    // accessory; no layer resets both.
    function updateAccessoryAdjust(layer, patch) {
      var pet = state.pets[state.activePetIdx];
      if (!pet) return false;
      if (ACCESSORY_LAYERS.indexOf(layer) === -1) {
        pet.accessoryAdjust = defaultAccessoryAdjust();
      } else {
        var next = Object.assign(defaultAccessoryAdjust(), pet.accessoryAdjust);
        next[layer] = clampLayerAdjust(Object.assign({}, next[layer], patch));
        pet.accessoryAdjust = next;
      }
      notify();
      return true;
    }

    // Moving only touches the in-memory offset - never reprocesses the cutout -
    // so the composite updates instantly. Size can't be changed.
    function updatePetPosition(index, position) {
      var pet = state.pets[index];
      if (!pet) return false;
      pet.adjustment = clampPosition(Object.assign({}, pet.adjustment, position));
      notify();
      return true;
    }

    function copyLookFromPet(source, pet) {
      if (state.extractionEnabled) {
        pet.mode = source.mode;
        pet.preferredMode = source.preferredMode;
      }
      if (state.filtersEnabled) {
        pet.style = source.style;
        pet.preferredStyle = source.preferredStyle;
      } else {
        pet.style = DEFAULT_STYLE;
      }
      pet.adjustment = clampPosition(source.adjustment);
      if (state.accessoriesEnabled && state.accessories.length) {
        pet.accessory = resolveAccessory(state.accessories, source.accessory);
      } else if (!state.accessoriesEnabled) {
        pet.accessory = ACCESSORY_NONE_KEY;
      }
    }

    // Selector-step batch action: copies mode, style, position and accessory
    // from the active pet to every uploaded pet, then reprocesses only what
    // changed. Accessories are overlay-only and never trigger reprocessing.
    async function applyLookToAllPets() {
      var source = state.pets[state.activePetIdx];
      if (!source || state.pets.length <= 1) return false;

      var activeIdx = state.activePetIdx;
      state.pets.forEach(function (pet) {
        copyLookFromPet(source, pet);
      });
      notify();

      await Promise.all(
        state.pets.map(function (pet, index) {
          return requestMode(index, pet.mode, false).catch(function () {
            return '';
          });
        })
      );

      await Promise.all(
        state.pets.map(function (_pet, index) {
          return requestStyle(index).catch(function () {
            return '';
          });
        })
      );

      if (state.activePetIdx !== activeIdx) {
        state.activePetIdx = activeIdx;
        notify();
      }
      return true;
    }

    // "Reset to original design": puts every pet back to the starting look
    // (mode Remove Background, style No Filter, no accessory, slot position)
    // without touching photos, pet count, or which pet is active. Style/accessory resolve against the current catalog so a
    // marketing-hidden default (e.g. No Filter) falls back the same way the
    // rest of the store does, instead of forcing a key that may no longer be
    // selectable. Reprocessing goes through the same requestMode/requestStyle
    // cache checks as applyLookToAllPets, so a pet that never left the
    // defaults skips the network round trip entirely.
    async function resetAllPetsToDefault() {
      if (!state.pets.length) return false;

      var activeIdx = state.activePetIdx;
      var defaultStyle = defaultStyleKey(state.styles);
      var defaultAccessory = defaultAccessoryKey(state.accessories);

      state.pets.forEach(function (pet) {
        pet.mode = DEFAULT_MODE;
        pet.preferredMode = DEFAULT_MODE;
        pet.style = defaultStyle;
        pet.preferredStyle = defaultStyle;
        pet.accessory = defaultAccessory;
        pet.adjustment = defaultAdjustment();
      });
      notify();

      await Promise.all(
        state.pets.map(function (pet, index) {
          return requestMode(index, pet.mode, false).catch(function () {
            return '';
          });
        })
      );

      await Promise.all(
        state.pets.map(function (_pet, index) {
          return requestStyle(index).catch(function () {
            return '';
          });
        })
      );

      if (state.activePetIdx !== activeIdx) {
        state.activePetIdx = activeIdx;
        notify();
      }
      return true;
    }

    // One request per pet and mode. Preview-loading and the launch refresh both
    // call prepare() when the studio opens; the second caller now shares the
    // request already on the wire instead of sending another resolve.
    function fetchMode(pet, mode, force) {
      if (force || !pet.pendingByMode[mode]) {
        var pending = Promise.resolve(processPet(pet.sourceImage, mode)).then(function (result) {
          var imageUrl = typeof result === 'string' ? result : result && result.imageUrl;
          if (!imageUrl) throw new Error('Processing returned no image');
          pet.processedByMode[mode] = imageUrl;
          return imageUrl;
        });
        var clear = function () {
          if (pet.pendingByMode[mode] === pending) delete pet.pendingByMode[mode];
        };
        pending.then(clear, clear);
        pet.pendingByMode[mode] = pending;
      }
      return pet.pendingByMode[mode];
    }

    // Fire-and-forget: the shopper's current mode is what the preview waits on.
    function warmOtherModes() {
      if (!state.eagerExtraction || !state.extractionEnabled) return;
      state.pets.forEach(function (pet, index) {
        EXTRACTION_MODES.forEach(function (mode) {
          if (mode === pet.mode || pet.processedByMode[mode]) return;
          fetchMode(pet, mode, false)
            .then(function (imageUrl) {
              // Its filters too, so switching mode shows them without a wait.
              if (state.eagerStyles && state.pets[index] === pet) {
                return prepareAllStyles(index, imageUrl);
              }
            })
            .catch(function () {
              return '';
            });
        });
      });
    }

    async function requestMode(index, mode, force) {
      var pet = state.pets[index];
      if (!pet) throw new Error('Pet is not available');

      var normalizedMode = normalizeMode(mode);
      if (!state.extractionEnabled) normalizedMode = DEFAULT_MODE;
      pet.mode = normalizedMode;
      if (state.extractionEnabled) pet.preferredMode = normalizedMode;
      pet.error = null;

      if (!force && pet.processedByMode[normalizedMode]) {
        pet.processedImage = pet.processedByMode[normalizedMode];
        pet.status = 'idle';
        notify();
        requestStyle(index).catch(function () {
          return '';
        });
        return pet.processedImage;
      }

      var sourceImage = pet.sourceImage;
      var requestId = ++pet.requestId;
      pet.status = 'processing';
      notify();

      try {
        var imageUrl = await fetchMode(pet, normalizedMode, force);

        var current = state.pets[index];
        if (
          !current ||
          current !== pet ||
          current.sourceImage !== sourceImage ||
          current.requestId !== requestId
        ) {
          return imageUrl;
        }

        current.processedByMode[normalizedMode] = imageUrl;
        if (current.mode === normalizedMode) {
          current.processedImage = imageUrl;
          current.status = 'idle';
          current.error = null;
        }
        notify();
        requestStyle(index).catch(function () {
          return '';
        });
        return imageUrl;
      } catch (error) {
        var failedPet = state.pets[index];
        if (failedPet === pet && failedPet.requestId === requestId) {
          failedPet.processedImage = '';
          failedPet.status = 'error';
          failedPet.error = error || new Error('Processing failed');
          notify();
        }
        throw error;
      }
    }

    function changeActiveMode(mode) {
      var index = state.activePetIdx;
      return requestMode(index, mode, false)
        .then(function (result) {
          // The new mode's cutout needs its own styles; ones made earlier for
          // this cutout are cached and skipped.
          if (state.eagerStyles) prepareAllStyles(index);
          return result;
        })
        .catch(function () {
          return false;
        });
    }

    function retryActiveMode() {
      var pet = state.pets[state.activePetIdx];
      return pet
        ? requestMode(state.activePetIdx, pet.mode, true).catch(function () {
            return false;
          })
        : Promise.resolve(false);
    }

    // One pet's provider failure must not sink the whole preview: the others
    // are already processed, and the failed pet reports itself in its own
    // control with a retry.
    /**
     * Extraction first, then every style for every pet. The style pass runs
     * eagerly so a shopper comparing Cartoon, Illustration and Airbrush waits
     * once during preparation rather than on each switch, and resolves to the
     * measured total the 10s target is judged against.
     */
    function prepare(context) {
      reconcile(context);
      warmOtherModes();
      return Promise.all(
        state.pets.map(function (pet, index) {
          return requestMode(index, pet.mode, false).catch(function () {
            return '';
          });
        })
      ).then(function (modes) {
        if (!state.eagerStyles) {
          beginPreparation(0);
          return modes;
        }
        beginPreparation(state.pets.length * generatableStyles().length);
        return prepareAllStylesForAllPets()
          .catch(function () {
            return state.styleLatency;
          })
          .then(function () {
            return modes;
          });
      });
    }

    return {
      applyLookToAllPets: applyLookToAllPets,
      changeActiveMode: changeActiveMode,
      changeActiveStyle: changeActiveStyle,
      prepare: prepare,
      reconcile: reconcile,
      rendererContext: rendererContext,
      resetAllPetsToDefault: resetAllPetsToDefault,
      retryActiveMode: retryActiveMode,
      retryActiveStyle: retryActiveStyle,
      isPreparedForDisplay: isPreparedForDisplay,
      prepareAllStyles: prepareAllStyles,
      getPreparationProgress: function () {
        return {
          completed: state.preparation.completed,
          total: state.preparation.total,
          ratio: state.preparation.total
            ? state.preparation.completed / state.preparation.total
            : 0,
        };
      },
      prepareAllStylesForAllPets: prepareAllStylesForAllPets,
      styleStateFor: styleStateFor,
      getStyleLatency: function () {
        return state.styleLatency;
      },
      selectPet: selectPet,
      setActiveAccessory: setActiveAccessory,
      updatePetPosition: updatePetPosition,
      setActiveStyle: setActiveStyle,
      snapshot: snapshot,
      updateAccessoryAdjust: updateAccessoryAdjust,
    };
  }

  var REPLACE_MESSAGES = {
    uploading: { text: 'Uploading your new photo…' },
    processing: { text: 'Updating your preview with the new photo…' },
    'upload-failed': {
      text: "We couldn't upload that photo. Your previous photo is still in place.",
      error: true,
    },
    'processing-failed': {
      text: "We couldn't update your new photo. Please try again.",
      error: true,
    },
  };

  function createControlsController(doc, options) {
    var config = options || {};
    var wiredControls = null;
    // Replacement spans two async hops the pet store cannot see on its own: the
    // PDP upload, then reprocessing. Tracked here so the shopper is told which
    // one is running rather than watching an unchanged photo.
    var replaceState = { phase: '', index: -1, previousSource: '' };
    var APPLY_ALL_CONFIRM_MS = 1800;
    // Marketing-editable defaults (data-apply-all-label and
    // data-apply-all-done-label on #pvApplyAllBtn); these are only the
    // fallbacks if those attributes are ever missing.
    var APPLY_ALL_DEFAULT_LABEL = 'Apply this look to all pets';
    var APPLY_ALL_DONE_LABEL = 'Applied to all pets';
    var applyAllConfirmTimer = null;
    var applyAllInFlight = false;
    var RESET_CONFIRM_MS = 1800;
    // Marketing-editable defaults (data-reset-label and data-reset-done-label
    // on #pvResetBtn); these are only the fallbacks if those attributes are
    // ever missing.
    var RESET_DEFAULT_LABEL = 'Reset to original design';
    var RESET_DONE_LABEL = 'Design reset';
    var resetConfirmTimer = null;
    var resetInFlight = false;

    function modalEl() {
      return doc && doc.getElementById('pvModal');
    }

    function extractionEnabled() {
      var modal = modalEl();
      return !modal || !modal.dataset || modal.dataset.extractionEnabled !== 'false';
    }

    function filtersEnabled() {
      var modal = modalEl();
      return !modal || !modal.dataset || modal.dataset.filtersEnabled !== 'false';
    }

    function accessoriesEnabled() {
      var modal = modalEl();
      return !modal || !modal.dataset || modal.dataset.accessoriesEnabled !== 'false';
    }

    // The catalog is delivered as a JSON script block (the same mechanism as
    // the placement catalog) and cannot change after load, so its text is read
    // once and reused.
    //
    // The raw text is what travels, not a parsed catalog: setContext parses
    // whatever it is handed, so parsing here made every field cross the parser
    // twice. Anything the parser renames on the way out then failed the second
    // pass - `back_layer` arrived as `backAsset` and was dropped - and the
    // catalog-wide settings beside the list (defaultPlacement,
    // backgroundLift) were lost entirely, because the first pass returns a
    // bare array and they live on the object wrapping it.
    var accessoryCatalogCache = null;

    function accessoryCatalog() {
      if (config.accessories != null) return config.accessories;
      if (accessoryCatalogCache === null) {
        var node = doc && doc.getElementById('pjDesignLabAccessoriesConfig');
        accessoryCatalogCache = (node && node.textContent) || '';
      }
      return accessoryCatalogCache;
    }

    var styleCatalogCache = null;

    // The store is handed the published list, not a resolved catalog: it resolves
    // once, and a resolved catalog put through resolveStyleCatalog a second time
    // cannot tell a No Filter marketing hid from one it never published.
    function publishedStyles() {
      if (config.styles != null) return config.styles;
      if (styleCatalogCache != null) return styleCatalogCache;

      var node = doc && doc.getElementById('pjDesignLabStylesConfig');
      styleCatalogCache = (node && node.textContent) || '';
      return styleCatalogCache;
    }

    function styleCatalog() {
      return resolveStyleCatalog(publishedStyles());
    }

    function withFeatureSettings(context) {
      return Object.assign({}, context || {}, {
        accessories: accessoryCatalog(),
        styles: publishedStyles(),
        extractionEnabled: extractionEnabled(),
        filtersEnabled: filtersEnabled(),
        accessoriesEnabled: accessoriesEnabled(),
      });
    }

    function pipelineModule() {
      return config.pipeline || root.PjDesignLabPipeline;
    }

    function apiToken() {
      return modalDataset().apiToken;
    }

    var SESSION_STORAGE_KEY = 'pjDesignLabSession';
    var designLabSession = null;

    function newSessionId() {
      var cryptoApi = root.crypto;
      if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
      return 'dl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    function designLabSessionId() {
      if (config.sessionId) return config.sessionId;
      if (designLabSession) return designLabSession;
      var storage = null;
      try {
        storage = root.sessionStorage || null;
        designLabSession = storage ? storage.getItem(SESSION_STORAGE_KEY) : null;
      } catch (error) {
        storage = null;
      }
      if (!designLabSession) {
        designLabSession = newSessionId();
        try {
          if (storage) storage.setItem(SESSION_STORAGE_KEY, designLabSession);
        } catch (error) {
          storage = null;
        }
      }
      return designLabSession;
    }

    function pipelineClient() {
      var pipeline = pipelineModule();
      var endpoint = modalDataset().pipelineUrl;
      if (!pipeline || !endpoint || !apiToken()) return null;

      return pipeline.createClient({
        fetch: config.fetch || root.fetch,
        endpoint: endpoint,
        token: apiToken(),
        sessionId: designLabSessionId(),
      });
    }

    // Legacy single-shot cutout. Kept as a fallback because the theme and CORS
    // deploy independently: a theme that shipped ahead of the pipeline endpoint
    // must still produce a preview rather than an error state.
    async function cutoutRequestBody(sourceImage, mode) {
      var body = {
        imageUrl: sourceImage,
        mode: mode === 'cutout' ? 'face_cutout' : 'background',
        sessionId: designLabSessionId(),
      };
      var helper = root.PjDesignLabPrintPpi;
      if (!helper || typeof helper.loadUrlSize !== 'function') return body;
      try {
        var size = await helper.loadUrlSize(sourceImage);
        if (size && size.width && size.height) {
          body.width = size.width;
          body.height = size.height;
        }
      } catch (error) {
        return body;
      }
      return body;
    }

    async function requestLegacyCutout(sourceImage, mode) {
      var endpoint = modalDataset().cutoutUrl;
      var token = apiToken();
      var fetchImpl = config.fetch || root.fetch;
      if (!endpoint || !token || typeof fetchImpl !== 'function') {
        throw new Error('Design Lab processing is not configured');
      }

      var response = await fetchImpl(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-token': token,
        },
        body: JSON.stringify(await cutoutRequestBody(sourceImage, mode)),
      });
      if (!response.ok) {
        var pipeline = pipelineModule();
        throw pipeline && typeof pipeline.requestError === 'function'
          ? await pipeline.requestError(response, 'Portrait processing failed')
          : new Error('Portrait processing failed');
      }
      return response.json();
    }

    // Both settings are marketing-owned metafields surfaced on the modal, read
    // once here rather than on every style request.
    function modalDataset() {
      var modal = modalEl();
      return (modal && modal.dataset) || {};
    }

    /**
     * Extraction now goes through the shared pipeline, so the portrait the
     * studio shows is the same stored artifact the vendor asset is built from -
     * and toggling back to a mode already resolved costs nothing, because the
     * pipeline addresses artifacts by content instead of re-cutting on every
     * switch the way the legacy endpoint did.
     *
     * Only this pet is described here. The slot is omitted deliberately: it
     * does not affect the cutout's address, and the full design -
     * which is what production reproduces - is described once at persist time
     * by stampDesignFingerprint.
     */
    async function requestProcessedImage(sourceImage, mode) {
      if (typeof config.processPet === 'function') {
        return config.processPet(sourceImage, mode);
      }

      var pipeline = pipelineModule();
      var client = pipelineClient();
      if (!pipeline || !client) return requestLegacyCutout(sourceImage, mode);

      var descriptor = pipeline.buildDescriptor({
        target: 'preview',
        garment: garmentDescriptor(),
        pets: [{ sourceImage: sourceImage, mode: mode }],
      });
      if (!pipeline.isResolvable(descriptor)) {
        return requestLegacyCutout(sourceImage, mode);
      }

      var resolved = await client.resolve(descriptor);
      var pet = resolved && resolved.pets && resolved.pets[0];
      var imageUrl = pet && pet.cutout && pet.cutout.imageUrl;
      if (!imageUrl) throw new Error('Portrait processing failed');

      return { imageUrl: imageUrl };
    }

    function createGeneratedStyles() {
      if (config.generatedStyles != null) return config.generatedStyles;

      var api = root.PjDesignLabGeneratedStyle;
      if (!api || typeof api.createGeneratedPipeline !== 'function') return null;

      var dataset = modalDataset();
      return api.createGeneratedPipeline({
        batchUrl: dataset.styleBatchUrl || '',
        cutoutUrl: dataset.cutoutUrl || '',
        apiToken: dataset.apiToken || '',
        productId: dataset.productId || '',
        sessionId: designLabSessionId(),
        timeoutMs:
          config.generationTimeoutMs || Number(dataset.generationTimeoutMs) || undefined,
      });
    }

    var generatedStyles = createGeneratedStyles();

    var store = createPetStore(requestProcessedImage, render, {
      styleImage: config.styleImage,
      generatedStyles: generatedStyles,
      recipeVersion: config.recipeVersion || modalDataset().styleRecipeVersion,
      eagerStyles:
        config.eagerStyles != null
          ? config.eagerStyles
          : modalDataset().eagerStyles === 'true',
      eagerExtraction:
        config.eagerExtraction != null
          ? config.eagerExtraction
          : modalDataset().eagerExtraction === 'true',
      generationTimeoutMs:
        config.generationTimeoutMs || Number(modalDataset().generationTimeoutMs),
      generationConcurrency:
        config.generationConcurrency ||
        Number(modalDataset().generationConcurrency),
    });

    /**
     * Garment-level context for the descriptor.
     *
     * Only the product and the background colour are included. Variant, size
     * and garment style live in the PDP form and change after the studio
     * opens, so stamping the value the modal happened to render would be worse
     * than omitting it: a stale garment field silently changes the descriptor
     * hash and would fail parity for a reason that has nothing to do with the
     * artwork. The line-item-properties story owns those selections and can
     * populate them from the live form.
     */
    function garmentDescriptor() {
      var snapshot = store.rendererContext();
      var previewRenderer = renderer();
      var color =
        previewRenderer && typeof previewRenderer.resolveColor === 'function'
          ? previewRenderer.resolveColor(doc, snapshot.color)
          : snapshot.color;

      return {
        productId: modalDataset().productId,
        backgroundColor: color,
      };
    }

    // The same slot resolution the renderer performs, so the descriptor
    // describes the composition the shopper is actually looking at rather than
    // a second guess at it.
    function placementResult() {
      var placementApi = config.placement || root.PjDesignLabPlacement;
      var loaded = placementApi && placementApi.current && placementApi.current();
      if (!loaded) return null;

      var snapshot = store.rendererContext();
      var previewRenderer = renderer();
      var color =
        previewRenderer && typeof previewRenderer.resolveColor === 'function'
          ? previewRenderer.resolveColor(doc, snapshot.color)
          : snapshot.color;
      var selectedStyle =
        previewRenderer && typeof previewRenderer.selectedStyle === 'function'
          ? previewRenderer.selectedStyle(doc)
          : '';

      return (placementApi.getSlotsOrDefault || placementApi.getSlots)(loaded.catalog, {
        color: color,
        style: selectedStyle,
        mediaId: snapshot.mediaId,
        petCount: snapshot.petCount,
      });
    }

    function resolvedSlots() {
      var placement = placementResult();
      return placement && placement.ok ? placement.slots : null;
    }

    function trackLab(name, detail) {
      var analytics = config.analytics || root.PjAnalytics;
      if (analytics) analytics.track(analytics.EVENTS[name], detail);
    }

    function activePetSnapshot() {
      var snapshot = store.snapshot();
      return snapshot.pets[snapshot.activePetIdx] || null;
    }

    function trackReplace(result, index) {
      trackLab('photoReplaced', { result: result, pet_index: index });
    }

    function replaceBusy() {
      return replaceState.phase === 'uploading' || replaceState.phase === 'processing';
    }

    function clearReplace() {
      replaceState = { phase: '', index: -1, previousSource: '' };
    }

    function beginReplace(index, previousSource) {
      replaceState = {
        phase: 'uploading',
        index: index,
        previousSource: previousSource || '',
      };
    }

    /**
     * A replacement is only confirmed once the active slot reports a different
     * source image. The uploader puts the previous photo back when an upload
     * fails or is cancelled, which surfaces here as an unchanged source.
     */
    function settleReplaceAfterReconcile() {
      if (!replaceBusy()) return;

      var pet = store.snapshot().pets[replaceState.index];
      if (!pet) {
        clearReplace();
        return;
      }

      if (pet.sourceImage !== replaceState.previousSource) {
        replaceState.phase = 'processing';
        replaceState.previousSource = pet.sourceImage;
        return;
      }

      if (replaceState.phase === 'uploading') {
        trackReplace('upload_failed', replaceState.index);
        replaceState = { phase: 'upload-failed', index: -1, previousSource: '' };
      }
    }

    // Reconcile notifies before reprocessing starts, so an idle pet that has no
    // processed output yet is still mid-replacement rather than finished.
    function settleReplaceAfterRender(pet) {
      if (replaceState.phase !== 'processing') return;

      if (!pet) {
        clearReplace();
        return;
      }
      if (pet.status === 'error') {
        trackReplace('processing_failed', replaceState.index);
        replaceState = { phase: 'processing-failed', index: -1, previousSource: '' };
        return;
      }
      if (pet.status !== 'processing' && pet.processedImage) {
        trackReplace('success', replaceState.index);
        clearReplace();
      }
    }

    // Mirrors the store's key so a snapshot's per-style map can be read without
    // reaching back into the store for state the snapshot already carries.
    function styleStateKey(pet, style) {
      return style + '|' + (recipeVersion() || '1') + '|' + pet.processedImage;
    }

    function recipeVersion() {
      return config.recipeVersion || modalDataset().styleRecipeVersion || '1';
    }

    function renderer() {
      return config.renderer || root.PjDesignLabRenderer;
    }

    function renderComposite() {
      var previewRenderer = renderer();
      if (!previewRenderer) return;
      var context = store.rendererContext();
      if (typeof previewRenderer.setContext === 'function') previewRenderer.setContext(context);
      var loaded = modalEl() && modalEl().querySelector('[data-pv-region="loaded"]');
      if (loaded && !loaded.hidden && typeof previewRenderer.render === 'function') {
        previewRenderer.render();
      }
    }

    function setStepNumber(id, value) {
      var node = doc.getElementById(id);
      if (node) node.textContent = String(value);
    }

    // Reads the marketing-editable labels straight off the button each time,
    // so a merchant's copy edit is honored even after a "done" flash reverts.
    function applyAllDefaultLabel() {
      var btn = doc.getElementById('pvApplyAllBtn');
      return (
        (btn && btn.dataset && btn.dataset.applyAllLabel) ||
        APPLY_ALL_DEFAULT_LABEL
      );
    }

    function applyAllDoneLabel() {
      var btn = doc.getElementById('pvApplyAllBtn');
      return (
        (btn && btn.dataset && btn.dataset.applyAllDoneLabel) ||
        APPLY_ALL_DONE_LABEL
      );
    }

    function showApplyAllConfirmation() {
      var btn = doc.getElementById('pvApplyAllBtn');
      var label = doc.getElementById('pvApplyAllLabel');
      if (!btn || !label) return;

      btn.classList.add('done');
      label.textContent = applyAllDoneLabel();
      if (applyAllConfirmTimer) clearTimeout(applyAllConfirmTimer);
      applyAllConfirmTimer = setTimeout(function () {
        applyAllConfirmTimer = null;
        btn.classList.remove('done');
        label.textContent = applyAllDefaultLabel();
      }, APPLY_ALL_CONFIRM_MS);
    }

    // Reads the marketing-editable labels straight off the button each time,
    // so a merchant's copy edit is honored even after a "done" flash reverts.
    function resetDefaultLabel() {
      var btn = doc.getElementById('pvResetBtn');
      return (btn && btn.dataset && btn.dataset.resetLabel) || RESET_DEFAULT_LABEL;
    }

    function resetDoneLabel() {
      var btn = doc.getElementById('pvResetBtn');
      return (
        (btn && btn.dataset && btn.dataset.resetDoneLabel) || RESET_DONE_LABEL
      );
    }

    function showResetConfirmation() {
      var btn = doc.getElementById('pvResetBtn');
      var label = doc.getElementById('pvResetLabel');
      if (!btn || !label) return;

      btn.classList.add('done');
      label.textContent = resetDoneLabel();
      if (resetConfirmTimer) clearTimeout(resetConfirmTimer);
      resetConfirmTimer = setTimeout(function () {
        resetConfirmTimer = null;
        btn.classList.remove('done');
        label.textContent = resetDefaultLabel();
      }, RESET_CONFIRM_MS);
    }

    function renderSelector(pets, activePetIdx) {
      var step = doc.getElementById('pvSelectStep');
      var row = doc.getElementById('pvPetRow');
      var applyAllStep = doc.getElementById('pvApplyAllStep');
      if (!step || !row) return;

      step.hidden = pets.length <= 1;
      if (applyAllStep) applyAllStep.hidden = pets.length <= 1;
      row.innerHTML = '';
      pets.forEach(function (pet, index) {
        var button = doc.createElement('button');
        button.type = 'button';
        button.className = 'pv-petthumb' + (index === activePetIdx ? ' active' : '');
        button.dataset.pet = String(index);
        button.setAttribute('aria-label', 'Edit pet ' + (index + 1));
        button.setAttribute('aria-pressed', String(index === activePetIdx));

        var image = doc.createElement('img');
        image.src = pet.sourceImage;
        image.alt = '';

        var badge = doc.createElement('span');
        badge.className = 'pv-petthumb-badge';
        badge.textContent = String(index + 1);

        button.appendChild(image);
        button.appendChild(badge);
        row.appendChild(button);
      });
    }

    function renderMode(pet) {
      var step = doc.getElementById('pvExtractionStep');
      var toggle = doc.getElementById('pvModeToggle');
      if (step) step.hidden = !extractionEnabled();
      if (!toggle || !pet) return;

      Array.prototype.forEach.call(toggle.querySelectorAll('[data-mode]'), function (button) {
        var active = normalizeMode(button.dataset.mode) === pet.mode;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      toggle.setAttribute('aria-busy', String(pet.status === 'processing'));

      var status = doc.getElementById('pvModeStatus');
      var retry = doc.getElementById('pvModeRetry');
      var errorCode = pet.error && pet.error.code;
      var unavailable = errorCode === 'GENERATION_UNAVAILABLE';
      var unusable = errorCode === 'UPLOAD_UNUSABLE';
      if (status) {
        status.textContent =
          pet.status === 'processing'
            ? 'Updating this pet…'
            : unavailable
              ? generationUnavailableLabel(status)
              : unusable
                ? unusablePhotoLabel(pet.error)
                : pet.error
                  ? "We couldn't update this pet. Please try again."
                  : '';
        status.classList.toggle('is-error', Boolean(pet.error) && !unavailable);
      }
      if (retry) retry.hidden = !pet.error || unusable;
    }

    function generationUnavailableLabel(status) {
      var feedback = status && status.parentNode;
      var data = (feedback && feedback.dataset) || {};
      return (
        data.generationUnavailableLabel ||
        'Photo processing is taking a short break. You can keep designing with the filters and check out as usual.'
      );
    }

    function unusablePhotoLabel(error) {
      var printPpi = root.PjDesignLabPrintPpi;
      if (!printPpi || typeof printPpi.messagesFor !== 'function') {
        return "We can't use this photo. Please use Replace photo to upload a clear photo of your pet.";
      }
      var copy = printPpi.messagesFor(
        { outcome: 'reject', code: 'UPLOAD_UNUSABLE', reason: error.reason },
        typeof printPpi.readConfig === 'function' ? printPpi.readConfig(doc) : {}
      );
      return [copy.title, copy.body].filter(Boolean).join('. ');
    }

    // Built from the catalog rather than markup, like the accessory grid below,
    // so marketing can add, rename, reorder or retire a style without a deploy.
    function renderStyle(pets, activePetIdx, catalog) {
      var step = doc.getElementById('pvStyleStep');
      if (!step) return;

      var pet = pets[activePetIdx];
      var entries = Array.isArray(catalog) ? catalog : [];
      // No Filter on its own is not a choice, so a catalog with nothing to switch
      // to leaves the step hidden.
      step.hidden = !filtersEnabled() || entries.length < 2 || !pet;

      var grid = doc.getElementById('pvStyleGrid');
      if (grid) grid.innerHTML = '';
      if (step.hidden || !grid) return;

      entries = entries.filter(function (entry) {
        if (entry.renderer !== STYLE_RENDERER_GENERATED) return true;
        if (!generatedStyles || typeof generatedStyles.statusFor !== 'function') {
          return true;
        }
        return (
          generatedStyles.statusFor(
            pet.processedImage,
            entry.recipeKey || entry.key
          ) !== 'unavailable'
        );
      });

      entries.forEach(function (entry) {
        var active = entry.key === pet.style;
        // Per-chip state, so the style being generated shows its own progress
        // while the rest of the Lab stays usable.
        var chipState = (pet.styleStates && pet.styleStates[styleStateKey(pet, entry.key)]) || 'idle';
        var button = doc.createElement('button');
        button.type = 'button';
        button.className =
          'pv-style-chip' +
          (entry.key === STYLE_NONE_KEY ? ' pv-style-chip--wide' : '') +
          (active ? ' active' : '') +
          (chipState === 'pending' ? ' is-pending' : '') +
          (chipState === 'error' ? ' is-error' : '');
        button.dataset.style = entry.key;
        button.dataset.styleState = chipState;
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-busy', String(chipState === 'pending'));

        var swatch = doc.createElement('span');
        swatch.className = 'pv-style-swatch sw-' + entry.key;
        var gradient = styleSwatchGradient(entry.swatch);
        if (entry.swatchImage) {
          var image = doc.createElement('img');
          image.src = entry.swatchImage;
          image.alt = '';
          swatch.className += ' pv-style-swatch--image';
          swatch.appendChild(image);
        } else if (gradient && swatch.style) {
          swatch.style.background = gradient;
        }

        var label = doc.createElement('span');
        label.className = 'pv-style-label';
        label.textContent = entry.label;

        button.appendChild(swatch);
        button.appendChild(label);
        grid.appendChild(button);
      });

      grid.setAttribute('aria-busy', String(pet.styleStatus === 'processing'));

      var status = doc.getElementById('pvStyleStatus');
      var retry = doc.getElementById('pvStyleRetry');
      var feedback = status && status.parentNode;
      var feedbackData = (feedback && feedback.dataset) || {};
      var errorReason = pet.styleError && pet.styleError.reason;
      var reasonLabel =
        errorReason === 'busy'
          ? feedbackData.styleErrorBusyLabel
          : errorReason === 'photo'
            ? feedbackData.styleErrorPhotoLabel
            : errorReason === 'unavailable'
              ? feedbackData.styleErrorUnavailableLabel
              : errorReason === 'down'
                ? feedbackData.styleErrorDownLabel
                : errorReason === 'paused'
                  ? feedbackData.generationUnavailableLabel ||
                    feedbackData.styleErrorDownLabel ||
                    'This style is temporarily unavailable. Please choose another style or check back later.'
                  : '';
      if (status) {
        status.textContent =
          pet.styleStatus === 'processing'
            ? feedbackData.stylePendingLabel || 'Applying this style…'
            : pet.styleError
              ? reasonLabel ||
                feedbackData.styleErrorLabel ||
                "We couldn't apply that style. Please try again."
              : '';
        if (status.classList) {
          status.classList.toggle('is-error', Boolean(pet.styleError) && errorReason !== 'paused');
        }
      }
      if (retry) {
        retry.hidden =
          !pet.styleError ||
          errorReason === 'photo' ||
          errorReason === 'unavailable' ||
          errorReason === 'down' ||
          errorReason === 'paused';
      }
    }

    // Built from the catalog rather than markup so marketing can add, rename,
    // reorder or retire an accessory without a deploy. Chips are real nodes
    // with textContent labels, so a merchant-edited label can never inject
    // markup into the sidebar.
    function renderAccessories(pets, activePetIdx, catalog) {
      var step = doc.getElementById('pvAccessoryStep');
      var grid = doc.getElementById('pvAccessoryGrid');
      if (!step || !grid) return;

      var pet = pets[activePetIdx];
      var entries = Array.isArray(catalog) ? catalog : [];
      step.hidden = !accessoriesEnabled() || !entries.length || !pet;
      grid.innerHTML = '';
      if (step.hidden) return;

      entries.forEach(function (entry) {
        var active = entry.key === pet.accessory;
        var button = doc.createElement('button');
        button.type = 'button';
        button.className =
          'pv-acc-chip' +
          (entry.key === ACCESSORY_NONE_KEY ? ' pv-acc-chip--wide' : '') +
          (active ? ' active' : '');
        button.dataset.accessory = entry.key;
        button.setAttribute('aria-pressed', String(active));

        var icon = doc.createElement('span');
        icon.className = 'pv-acc-ico' + (entry.icon ? '' : ' pv-acc-ico--none');
        if (entry.icon) {
          var image = doc.createElement('img');
          image.src = entry.icon;
          image.alt = '';
          icon.appendChild(image);
        }

        var label = doc.createElement('span');
        label.className = 'pv-acc-label';
        label.textContent = entry.label;

        button.appendChild(icon);
        button.appendChild(label);
        grid.appendChild(button);
      });
      renderAccessoryAdjust(pet);
    }

    // TEST ONLY: the on-preview editing hint, and a reset once the shopper
    // has moved the accessory.
    function renderAccessoryAdjust(pet) {
      var panel = doc.getElementById('pvAccAdjust');
      if (!panel) return;
      panel.hidden = !pet || !pet.accessory || pet.accessory === ACCESSORY_NONE_KEY;
      var reset = doc.getElementById('pvAccAdjustReset');
      if (reset) reset.hidden = !(pet && accessoryAdjusted(pet.accessoryAdjust));
    }

    function renderReplace(petCount) {
      var singleRow = doc.getElementById('pvReplaceRow');
      var multiButton = doc.getElementById('pvReplaceBtn');
      var singleButton = doc.getElementById('pvReplaceBtn1');
      var status = doc.getElementById('pvReplaceStatus');
      var message = REPLACE_MESSAGES[replaceState.phase];

      if (singleRow) singleRow.hidden = petCount !== 1;
      if (multiButton) multiButton.hidden = petCount <= 1;

      // Blocked while busy so a second pick cannot race the upload already in
      // flight and leave the studio showing the wrong photo.
      [singleButton, multiButton].forEach(function (button) {
        if (button) button.disabled = replaceBusy();
      });

      if (!status) return;
      status.textContent = message ? message.text : '';
      status.hidden = !message;
      status.classList.toggle('is-error', Boolean(message && message.error));
    }

    // Both persist helpers below normally find an input the uploader already
    // rendered inside the product form. When they have to create one, it has to
    // land somewhere Add to Cart actually looks: the sweep reads
    // `[name^="properties["]` from `.main-product-section`, so an input on
    // `document.body` renders with the right value and is then dropped at
    // checkout. See propertyHost in pj-design-lab-pipeline.js.
    function appendPropertyInput(input) {
      var pipeline = pipelineModule();
      var host =
        (pipeline &&
          typeof pipeline.propertyHost === 'function' &&
          pipeline.propertyHost(doc)) ||
        doc.body;
      if (host && typeof host.appendChild === 'function') host.appendChild(input);
    }

    // The Design Lab previews a processed image but, on its own, never wrote
    // it anywhere the order would see it - persisting to a hidden cart
    // property here (mirroring the non-pajama flow's `_cutout_image_url_${i}`
    // convention) is what makes "what the shopper approved" reach checkout.
    function persistProcessedImage(index, imageUrl) {
      if (!doc || !imageUrl || typeof doc.getElementById !== 'function') return;
      var uploaderIndex = index + 1;
      var elementId = 'cutoutHiddenInputSimple-' + uploaderIndex;
      var input = doc.getElementById(elementId);
      if (!input) {
        if (typeof doc.createElement !== 'function') return;
        input = doc.createElement('input');
        input.type = 'hidden';
        input.id = elementId;
        input.name = 'properties[_cutout_image_url_' + uploaderIndex + ']';
        appendPropertyInput(input);
      }
      input.value = imageUrl;
    }

    // The customer's drag-to-reposition offset on its own hidden property, so
    // production places the (unmodified) cutout where the Lab previewed it.
    // Written only once the pet has moved; blanked when it is back in its slot.
    function persistDesignAdjustment(index, adjustment) {
      if (!doc || typeof doc.getElementById !== 'function') return;
      var uploaderIndex = index + 1;
      var elementId = 'cutoutAdjustmentHiddenInput-' + uploaderIndex;
      var input = doc.getElementById(elementId);
      var moved = !isDefaultAdjustment(adjustment);

      if (!input) {
        if (!moved || typeof doc.createElement !== 'function') return;
        input = doc.createElement('input');
        input.type = 'hidden';
        input.id = elementId;
        input.name = 'properties[_cutout_adjustment_' + uploaderIndex + ']';
        appendPropertyInput(input);
      }
      input.value = moved ? JSON.stringify(adjustment) : '';
    }

    // Deduped by descriptor identity: render() runs on every state change, and
    // re-resolving an unchanged design would be a network call per keystroke.
    var stampedDescriptor = '';
    var stampRefusedUntil = 0;
    var stampTimer = null;
    var pendingStampPets = null;

    // Stamps the latest design once changes have paused for
    // STAMP_DEBOUNCE_MS; only the last design in a burst is resolved.
    function stampDesignFingerprint(pets) {
      var wait = config.stampDebounceMs != null ? Number(config.stampDebounceMs) : STAMP_DEBOUNCE_MS;
      if (!(wait > 0)) return stampDesignFingerprintNow(pets);
      pendingStampPets = pets;
      if (stampTimer) root.clearTimeout(stampTimer);
      stampTimer = root.setTimeout(flushDesignStamp, wait);
    }

    // Sends a pending stamp now - Finalize does, so a design saved inside the
    // quiet window is not left waiting on it.
    function flushDesignStamp() {
      if (stampTimer) root.clearTimeout(stampTimer);
      stampTimer = null;
      var pets = pendingStampPets;
      pendingStampPets = null;
      if (pets) stampDesignFingerprintNow(pets);
    }

    /**
     * Describes the whole finalized design to the pipeline and writes the
     * resulting version + per-stage addresses to the cart.
     *
     * This is what makes the CORS-side parity check mean anything: without the
     * stamp on the line item, production has no way to tell whether the asset
     * it received is the one the customer approved. Resolving here is cheap -
     * every cutout is already stored from the extraction step, so this is a
     * cache read that returns addresses.
     *
     * A partial design is never stamped. Stamping a design with a failed or
     * still-processing pet would assert parity over artwork that does not exist
     * yet.
     */
    function stampDesignFingerprintNow(pets) {
      var pipeline = pipelineModule();
      var client = pipelineClient();
      if (!pipeline || !client || !pets.length) return;

      var ready = pets.every(function (pet) {
        return pet.status === 'idle' && pet.processedImage && !pet.error;
      });
      if (!ready) return;

      var descriptor = pipeline.buildDescriptor({
        target: 'final',
        garment: garmentDescriptor(),
        slots: resolvedSlots(),
        pets: pets.map(function (pet) {
          return {
            sourceImage: pet.sourceImage,
            mode: pet.mode,
            style: pet.style,
            accessory: pet.accessory,
            adjustment: pet.adjustment || defaultAdjustment(),
          };
        }),
      });
      if (!pipeline.isResolvable(descriptor)) return;

      var identity = JSON.stringify(descriptor);
      if (identity === stampedDescriptor) return;
      if (Date.now() < stampRefusedUntil) return;
      stampedDescriptor = identity;

      client
        .resolve(descriptor)
        .then(function (resolved) {
          if (!pipeline.stamp(doc, resolved)) stampedDescriptor = '';
        })
        .catch(function (error) {
          if (error && error.code === 'GENERATION_UNAVAILABLE') {
            stampRefusedUntil = Date.now() + STAMP_REFUSED_RETRY_MS;
          }
          // Retry on the next design change rather than leaving a stale stamp
          // pinned to a descriptor that never resolved.
          stampedDescriptor = '';
        });
    }

    // Key, not label (labels change); version pinned since recipes can be republished.
    function persistPetStyle(index, styleKey, version) {
      if (!doc || typeof doc.getElementById !== 'function') return;
      var uploaderIndex = index + 1;
      var elementId = 'petStyleHiddenInput-' + uploaderIndex;
      var input = doc.getElementById(elementId);
      var value =
        styleKey && styleKey !== DEFAULT_STYLE
          ? styleKey + (version ? '@' + version : '')
          : '';

      if (!input) {
        if (!value || typeof doc.createElement !== 'function') return;
        input = doc.createElement('input');
        input.type = 'hidden';
        input.id = elementId;
        input.name = 'properties[_pet_style_' + uploaderIndex + ']';
        appendPropertyInput(input);
      }

      input.value = value;
    }

    // Generated artwork is the print asset; filters are preview-only.
    function persistStyledImage(index, imageUrl) {
      if (!doc || typeof doc.getElementById !== 'function') return;
      var uploaderIndex = index + 1;
      var elementId = 'styledImageHiddenInput-' + uploaderIndex;
      var input = doc.getElementById(elementId);

      if (!input) {
        if (!imageUrl || typeof doc.createElement !== 'function') return;
        input = doc.createElement('input');
        input.type = 'hidden';
        input.id = elementId;
        input.name = 'properties[_styled_image_url_' + uploaderIndex + ']';
        appendPropertyInput(input);
      }

      input.value = imageUrl || '';
    }

    function generatedVersion(pet, catalog) {
      if (!generatedStyles || typeof generatedStyles.cached !== 'function') return null;
      var hit = generatedStyles.cached(pet.processedImage, styleRecipeKey(catalog, pet.style));
      return hit && hit.version != null ? hit.version : null;
    }

    function persistProcessedImages(pets, catalog) {
      pets.forEach(function (pet, index) {
        if (pet.status === 'idle' && pet.processedImage && !pet.error) {
          persistProcessedImage(index, pet.processedImage);
          persistDesignAdjustment(index, pet.adjustment);

          var generated = isGeneratedStyle(catalog, pet.style);
          // Pending or failed artwork falls back to the cutout so checkout never bills missing art.
          var ready = Boolean(generated && pet.styledImage);

          persistPetStyle(
            index,
            generated && !ready ? '' : pet.style,
            ready ? generatedVersion(pet, catalog) : null
          );
          persistStyledImage(index, ready ? pet.styledImage : '');
        }
      });
      persistDesignSession(pets);
      stampDesignFingerprint(pets);
    }

    function persistDesignSession(pets) {
      if (!doc || typeof doc.getElementById !== 'function' || !pets.length) return;
      var input = doc.getElementById('pjDesignLabSessionInput');
      if (!input) {
        if (typeof doc.createElement !== 'function') return;
        input = doc.createElement('input');
        input.type = 'hidden';
        input.id = 'pjDesignLabSessionInput';
        input.name = 'properties[_design_lab_session]';
        appendPropertyInput(input);
      }
      input.value = designLabSessionId();
    }

    // The committed design, kept apart from the editable store so closing the
    // modal without finalizing never changes it. Re-finalizing replaces it.
    var committedDesign = null;

    // Same readiness rule as stampDesignFingerprint: never commit a pet that
    // is failed or still processing, including a style that failed or has not
    // arrived: the shopper retries or picks another rather than finalizing
    // without it.
    function canFinalize() {
      var pets = store.snapshot().pets;
      return (
        pets.length > 0 &&
        pets.every(function (pet) {
          return (
            pet.status === 'idle' &&
            pet.processedImage &&
            !pet.error &&
            pet.styleStatus === 'idle'
          );
        })
      );
    }

    // Only what changes the printed design. Zoom, active pet and processing
    // status are view state, so reopening or finishing a cutout never un-saves.
    function designKey(snapshot) {
      // Color is deliberately absent: a background change re-tints the saved
      // design instead of un-saving it (see invalidateFinalizedIfChanged).
      return JSON.stringify([
        snapshot.accessories,
        snapshot.accessorySettings,
        snapshot.pets.map(function (pet) {
          return [
            pet.sourceImage,
            pet.mode,
            pet.style,
            pet.accessory,
            pet.adjustment,
          ];
        }),
      ]);
    }

    function announceFinalizedChange() {
      if (typeof root.CustomEvent !== 'function' || !root.dispatchEvent) return;
      root.dispatchEvent(
        new root.CustomEvent('pj-design-lab:finalized-change', {
          detail: { finalized: Boolean(committedDesign) },
        })
      );
    }

    function finalize() {
      if (!canFinalize()) return null;
      flushDesignStamp();
      var snapshot = store.snapshot();
      var design = JSON.parse(JSON.stringify(snapshot));
      committedDesign = {
        version: committedDesign ? committedDesign.version + 1 : 1,
        finalizedAt: Date.now(),
        petCount: design.pets.length,
        design: design,
        key: designKey(snapshot),
      };
      announceFinalizedChange();
      return committedDesign;
    }

    // A saved design stops being saved the moment the live design diverges from it.
    function invalidateFinalizedIfChanged() {
      if (!committedDesign) return;
      var live = store.snapshot();
      if (designKey(live) === committedDesign.key) {
        // Only the background moved: keep the design but carry the new color,
        // so the cart never serializes a snapshot tinted for the old one.
        // A color with no placement template cannot hold the design, though.
        var coverage = placementResult();
        if (coverage && !coverage.ok) {
          committedDesign = null;
          announceFinalizedChange();
          return;
        }
        committedDesign.design.color = live.color;
        return;
      }
      committedDesign = null;
      announceFinalizedChange();
    }

    function invalidateFinalized() {
      if (!committedDesign) return;
      committedDesign = null;
      announceFinalizedChange();
    }

    function renderFinalize() {
      var btn = doc.getElementById('pvFinalize');
      if (btn) btn.disabled = !canFinalize();
    }

    function render() {
      if (!doc) return;
      var state = store.snapshot();
      settleReplaceAfterRender(state.pets[replaceState.index]);
      var isMulti = state.pets.length > 1;
      var extractionVisible = state.extractionEnabled;
      var filtersVisible = state.filtersEnabled;
      var accessoriesVisible = state.accessoriesEnabled;
      var numbers = stepNumbers(
        isMulti,
        extractionVisible,
        filtersVisible,
        accessoriesVisible
      );
      renderSelector(state.pets, state.activePetIdx);
      renderMode(state.pets[state.activePetIdx]);
      renderStyle(state.pets, state.activePetIdx, state.styles);
      renderAccessories(state.pets, state.activePetIdx, state.accessories);
      renderReplace(state.pets.length);
      setStepNumber('pvNumCutout', numbers.extraction);
      setStepNumber('pvNumStyle', numbers.style);
      setStepNumber('pvNumAccessory', numbers.accessory);
      renderComposite();
      renderFinalize();
      invalidateFinalizedIfChanged();
      persistProcessedImages(state.pets, state.styles);
    }

    function onControlsClick(event) {
      var replaceButton =
        event.target.closest &&
        event.target.closest('#pvReplaceBtn, #pvReplaceBtn1');
      if (replaceButton) {
        var replaceInput = doc.getElementById('pvReplaceInput');
        if (replaceInput && typeof replaceInput.click === 'function') {
          replaceInput.value = '';
          replaceInput.click();
        }
        return;
      }

      var petButton = event.target.closest && event.target.closest('[data-pet]');
      if (petButton) {
        store.selectPet(Number(petButton.dataset.pet));
        return;
      }

      var applyAllBtn = event.target.closest && event.target.closest('#pvApplyAllBtn');
      if (applyAllBtn) {
        if (
          applyAllBtn.classList.contains('done') ||
          applyAllInFlight ||
          typeof store.applyLookToAllPets !== 'function'
        ) {
          return;
        }

        applyAllInFlight = true;
        applyAllBtn.disabled = true;
        store
          .applyLookToAllPets()
          .then(function (applied) {
            if (applied) {
              trackLab('applyAll');
              showApplyAllConfirmation();
              renderComposite();
            }
          })
          .finally(function () {
            applyAllInFlight = false;
            applyAllBtn.disabled = false;
          });
        return;
      }

      var resetBtn = event.target.closest && event.target.closest('#pvResetBtn');
      if (resetBtn) {
        if (
          resetBtn.classList.contains('done') ||
          resetInFlight ||
          typeof store.resetAllPetsToDefault !== 'function'
        ) {
          return;
        }

        resetInFlight = true;
        resetBtn.disabled = true;
        store
          .resetAllPetsToDefault()
          .then(function (didReset) {
            if (didReset) {
              trackLab('reset');
              showResetConfirmation();
              renderComposite();
            }
          })
          .finally(function () {
            resetInFlight = false;
            resetBtn.disabled = false;
          });
        return;
      }

      var modeButton = event.target.closest && event.target.closest('[data-mode]');
      if (modeButton) {
        var modePet = activePetSnapshot();
        var nextMode = normalizeMode(modeButton.dataset.mode);
        if (modePet && modePet.mode !== nextMode) {
          trackLab('extractionModeChanged', { mode: nextMode, prev_mode: modePet.mode || '' });
        }
        store.changeActiveMode(modeButton.dataset.mode);
        return;
      }

      var styleButton = event.target.closest && event.target.closest('[data-style]');
      if (styleButton) {
        var stylePet = activePetSnapshot();
        var previousStyle = stylePet ? stylePet.style : '';
        store.changeActiveStyle(styleButton.dataset.style);
        stylePet = activePetSnapshot();
        if (stylePet && stylePet.style !== previousStyle) {
          trackLab('filterSelected', { style_key: stylePet.style, prev_style_key: previousStyle || '' });
        }
        return;
      }

      var styleRetry = event.target.closest && event.target.closest('#pvStyleRetry');
      if (styleRetry) {
        store.retryActiveStyle();
        return;
      }

      if (event.target.closest && event.target.closest('#pvAccAdjustReset')) {
        store.updateAccessoryAdjust(null);
        return;
      }

      var accessoryButton =
        event.target.closest && event.target.closest('[data-accessory]');
      if (accessoryButton) {
        var accessoryPet = activePetSnapshot();
        var previousAccessory = accessoryPet ? accessoryPet.accessory : '';
        store.setActiveAccessory(accessoryButton.dataset.accessory);
        accessoryPet = activePetSnapshot();
        if (accessoryPet && accessoryPet.accessory !== previousAccessory) {
          trackLab('accessorySelected', {
            accessory_key: accessoryPet.accessory,
            prev_accessory_key: previousAccessory || '',
          });
        }
        return;
      }

      var retry = event.target.closest && event.target.closest('#pvModeRetry');
      if (retry) store.retryActiveMode();
    }

    function onControlsChange(event) {
      var input = event && event.target;
      if (!input || input.id !== 'pvReplaceInput' || !input.files || !input.files.length) {
        return;
      }

      var snapshot = store.snapshot();
      var activeIndex = snapshot.activePetIdx;
      var activePet = snapshot.pets[activeIndex];
      var launch = config.previewLaunch || root.PjDesignLabPreviewLaunch;
      var handedOff =
        launch &&
        typeof launch.replacePetPhoto === 'function' &&
        launch.replacePetPhoto(activeIndex, input.files);

      if (handedOff) beginReplace(activeIndex, activePet && activePet.sourceImage);
      else replaceState = { phase: 'upload-failed', index: -1, previousSource: '' };
      render();
    }

    function setContext(context) {
      store.reconcile(withFeatureSettings(context));
      settleReplaceAfterReconcile();
      render();
      return store.rendererContext();
    }

    // store.prepare reconciles too, but only after this returns. Reconciling
    // here first lets the replacement status move from uploading to processing
    // before the first request goes out.
    function prepare(context) {
      store.reconcile(withFeatureSettings(context));
      settleReplaceAfterReconcile();
      render();

      return store.prepare(withFeatureSettings(context)).then(function () {
        renderComposite();
        return store.rendererContext();
      });
    }

    function init() {
      var modal = modalEl();
      var controls = modal && modal.querySelector('[data-pv-region="controls"]');
      if (!controls) return;
      if (wiredControls !== controls) {
        wiredControls = controls;
        controls.addEventListener('click', onControlsClick);
        controls.addEventListener('change', onControlsChange);
      }
      render();
    }

    return {
      accessoryCatalog: accessoryCatalog,
      applyLookToAllPets: store.applyLookToAllPets,
      changeActiveStyle: store.changeActiveStyle,
      isPreparedForDisplay: store.isPreparedForDisplay,
      extractionEnabled: extractionEnabled,
      filtersEnabled: filtersEnabled,
      accessoriesEnabled: accessoriesEnabled,
      canFinalize: canFinalize,
      finalize: finalize,
      invalidateFinalized: invalidateFinalized,
      finalizedDesign: function () {
        return committedDesign;
      },
      init: init,
      prepare: prepare,
      replaceStatus: function () {
        return replaceState.phase;
      },
      resetAllPetsToDefault: store.resetAllPetsToDefault,
      retryActiveStyle: store.retryActiveStyle,
      setActiveAccessory: store.setActiveAccessory,
      updatePetPosition: store.updatePetPosition,
      setContext: setContext,
      snapshot: store.snapshot,
      updateAccessoryAdjust: store.updateAccessoryAdjust,
      styleCatalog: styleCatalog,
    };
  }

  var controller = root.document ? createControlsController(root.document) : null;

  if (controller) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', controller.init, { once: true });
    } else {
      controller.init();
    }
  }

  return {
    ACCESSORY_NONE_KEY: ACCESSORY_NONE_KEY,
    DEFAULT_MODE: DEFAULT_MODE,
    DEFAULT_STYLE: DEFAULT_STYLE,
    DEFAULT_STYLE_CATALOG: DEFAULT_STYLE_CATALOG,
    createControlsController: createControlsController,
    createPetStore: createPetStore,
    defaultAdjustment: defaultAdjustment,
    clampPosition: clampPosition,
    replacePetSource: replacePetSource,
    normalizeMode: normalizeMode,
    normalizeStyle: normalizeStyle,
    normalizeAccessoryKey: normalizeAccessoryKey,
    parseStyleCatalog: parseStyleCatalog,
    parseStyleSwatch: parseStyleSwatch,
    resolveStyleCatalog: resolveStyleCatalog,
    resolveStyle: resolveStyle,
    defaultStyleKey: defaultStyleKey,
    styleKeys: styleKeys,
    styleSwatchGradient: styleSwatchGradient,
    styleTreatment: styleTreatment,
    parseAccessoryCatalog: parseAccessoryCatalog,
    parseAccessoryPlacement: parseAccessoryPlacement,
    parseAccessorySettings: parseAccessorySettings,
    liftAccessoryPlacement: liftAccessoryPlacement,
    parseAccessoryPetBox: parseAccessoryPetBox,
    defaultAccessoryKey: defaultAccessoryKey,
    resolveAccessory: resolveAccessory,
    stepNumbers: stepNumbers,
    init: controller && controller.init,
    prepare: controller && controller.prepare,
    setContext: controller && controller.setContext,
    snapshot: controller && controller.snapshot,
    updateAccessoryAdjust: controller && controller.updateAccessoryAdjust,
    finalize: controller && controller.finalize,
    isPreparedForDisplay: controller && controller.isPreparedForDisplay,
    finalizedDesign: controller && controller.finalizedDesign,
    invalidateFinalized: controller && controller.invalidateFinalized,
    applyLookToAllPets: controller && controller.applyLookToAllPets,
    extractionEnabled: controller && controller.extractionEnabled,
    filtersEnabled: controller && controller.filtersEnabled,
    accessoriesEnabled: controller && controller.accessoriesEnabled,
    resetAllPetsToDefault: controller && controller.resetAllPetsToDefault,
    changeActiveStyle: controller && controller.changeActiveStyle,
    retryActiveStyle: controller && controller.retryActiveStyle,
    styleCatalog: controller && controller.styleCatalog,
    setActiveAccessory: controller && controller.setActiveAccessory,
    updatePetPosition: controller && controller.updatePetPosition,
  };
});
